import React, { useState, useEffect, useRef, useMemo, Fragment, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemButton
} from '@mui/material';
import {
  Edit,
  Delete,
  ContentCopy,
  Print,
  Article,
  ArrowBack,
  Add as AddIcon,
  Remove as RemoveIcon,
  Save,
  Cancel,
  TextSnippet,
  CloudUpload,
  OpenInNew,
  Settings
} from '@mui/icons-material';
import { orderService } from '../services/orderService';
import { addressService } from '../services/customerService';
import CustomerAddressesModal from '../components/CustomerAddressesModal';
import { attachmentService } from '../services/attachmentService';
import {
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_LABEL,
} from '../constants/attachmentLimits';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { inventoryService } from '../services/inventoryService';
import { packingSlipService } from '../services/packingSlipService';
import { customerPaymentService } from '../services/customerPaymentService';
import AttachmentCategoryChips from '../components/AttachmentCategoryChips';
import NewPartModal from '../components/NewPartModal';
import NewServiceModal from '../components/NewServiceModal';
import OrderLineMaterialsRefAutocomplete from '../components/OrderLineMaterialsRefAutocomplete';
import LineItemPartAutocomplete from '../components/LineItemPartAutocomplete';
import LineItemServiceAutocomplete from '../components/LineItemServiceAutocomplete';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import { applyCatalogRecordToLineRow } from '../utils/catalogSearch';
import { format } from 'date-fns';
import { formatDateOnly, formatDateForInput } from '../utils/dateUtils';
import axios from 'axios';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import { formatQuantityDisplay, formatLineItemQtyEditValue } from '../utils/quantityDisplay';
import { rasterizePdfBlobToJpegDataUrls } from '../utils/rasterizePdfForPrint';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import {
  getLinkedPoStatusPresentation,
  procurementSummaryFieldsRowSx,
} from '../components/procurement/ProcurementSummaryBar';
import QuoteOrderLineagePanel from '../components/QuoteOrderLineagePanel';
import {
  FieldWorkOrderDetailCard,
  FieldWorkOrderPrintSection,
  fieldWorkFieldsFromOrder
} from '../components/fieldWork/FieldWorkScopeBlocks';
import { resolveProposalTotalAmount } from '../utils/proposalBreakdown';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import WorkHoursTab from '../components/orders/WorkHoursTab';
import { useAuth } from '../hooks/useAuth';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import {
  computeLineItemTaxAmount,
  computeLineTotalWithPerLineTax,
  formatLineItemTaxDollars,
  handleLineTaxPercentArrowKeyDown,
  lineTaxPatchFromAmountInput,
  lineTaxPatchFromPercentInput,
  lineTaxPercentFieldValue,
  lineTaxPercentInputPropsExtra,
  lineTaxPercentLabel,
  lineItemTaxDollarsEditFieldSx,
  lineItemTaxPercentEditFieldSx,
  documentFooterTaxFieldSx,
  documentFooterTaxRatePercentFieldSx,
  formatTaxPercentDisplayValue,
  sanitizeTaxPercentInputValue,
  normalizeLineTaxModeUi,
  normalizeTaxBasisUi
} from '../utils/lineItemTax';

const ORDER_TM_LINE_TABLE_COLS = 12;
const ORDER_TM_FOOTER_LABEL_COLSPAN = 8;

function computeOrderTmLineSubtotal(row) {
  const q = Number(row?.quantity_required ?? row?.quantity) || 0;
  const p = Number(row?.unit_price) || 0;
  return Math.round(q * p * 100) / 100;
}

function buildWorkOrderLineItemTaxPayload(item) {
  const ltMode = normalizeLineTaxModeUi(item?.line_tax_mode);
  let line_tax_percent = null;
  let line_tax_fixed_amount = null;
  if (ltMode === 'percent') {
    const t = String(item?.line_tax_percent ?? '').trim();
    if (t !== '') {
      const p = parseFloat(t);
      if (Number.isFinite(p) && p >= 0) line_tax_percent = p;
    }
  } else if (ltMode === 'fixed') {
    const t = String(item?.line_tax_fixed_amount ?? '').trim();
    if (t !== '') {
      const f = parseFloat(t);
      if (Number.isFinite(f) && f >= 0) line_tax_fixed_amount = Math.round(f * 100) / 100;
    }
  }
  return {
    line_tax_mode: ltMode,
    line_tax_percent,
    line_tax_fixed_amount
  };
}

const EDIT_LOCK_HEARTBEAT_MS = 3 * 60 * 1000;
const EDIT_LOCK_POLL_MS = 30 * 1000;

function editLockDisplayName(lock) {
  if (!lock) return '';
  const fn = String(lock.editor_first_name || '').trim();
  const ln = String(lock.editor_last_name || '').trim();
  const name = `${fn} ${ln}`.trim();
  if (name) return name;
  const em = String(lock.editor_email || '').trim();
  return em || 'Another user';
}

function normalizeOrderTaxModeUi(v) {
  const s = String(v == null || v === '' ? 'none' : v).trim().toLowerCase();
  if (s === 'percent') return 'percent';
  if (s === 'fixed') return 'fixed';
  return 'none';
}

function normalizeOrderDiscountModeUi(v) {
  const s = String(v == null || v === '' ? 'none' : v).trim().toLowerCase();
  if (s === 'percent' || s === 'percentage' || s === '%') return 'percent';
  if (s === 'amount' || s === 'fixed' || s === 'dollar' || s === '$') return 'amount';
  return 'none';
}

function computeOrderDisplayTax(subtotal, mode, percentStr, fixedStr) {
  const sub = Number(subtotal) || 0;
  const m = normalizeOrderTaxModeUi(mode);
  if (m === 'percent') {
    const t = String(percentStr ?? '').trim();
    if (t === '') return 0;
    const p = parseFloat(t);
    if (!Number.isFinite(p) || p < 0) return 0;
    return Math.round(sub * (p / 100) * 100) / 100;
  }
  if (m === 'fixed') {
    const t = String(fixedStr ?? '').trim();
    if (t === '') return 0;
    const f = parseFloat(t);
    if (!Number.isFinite(f) || f < 0) return 0;
    return Math.round(f * 100) / 100;
  }
  return 0;
}

function computeOrderDisplayDiscount(merchandise, mode, percentStr, fixedStr) {
  const base = Math.max(0, Number(merchandise) || 0);
  const m = normalizeOrderDiscountModeUi(mode);
  let raw = 0;
  if (m === 'percent') {
    const t = String(percentStr ?? '').trim();
    if (t === '') return 0;
    const p = parseFloat(t);
    if (!Number.isFinite(p) || p < 0) return 0;
    raw = Math.round(base * (p / 100) * 100) / 100;
  } else if (m === 'amount') {
    const t = String(fixedStr ?? '')
      .trim()
      .replace(/[$,\s]/g, '');
    if (t === '') return 0;
    const f = parseFloat(t);
    if (!Number.isFinite(f) || f < 0) return 0;
    raw = Math.round(f * 100) / 100;
  }
  return Math.min(base, raw);
}

/** Human-readable status for the completion banner (uses stored work_orders.status). */
function formatWorkOrderStatusForCompletionBanner(statusRaw) {
  if (statusRaw == null || String(statusRaw).trim() === '') return 'Open';
  const s = String(statusRaw).trim();
  const lower = s.toLowerCase();
  if (lower === 'open') return 'Open';
  if (lower === 'in_progress') return 'In progress';
  if (lower === 'waiting_external') return 'Waiting external';
  if (s === 'Ready to Ship') return 'Ready to Ship';
  if (s === 'Partial') return 'Partial';
  if (lower === 'shipped') return 'Shipped';
  if (lower === 'completed') return 'Completed';
  if (lower === 'cancelled') return 'Cancelled';
  const titled = lower.replace(/_/g, ' ');
  return titled.charAt(0).toUpperCase() + titled.slice(1);
}

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { registerTabTitle, registerDirtyState } = useDocumentTabs();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const printRequested = new URLSearchParams(window.location.search).get('print') === '1';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(1);
  const [newLineItems, setNewLineItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editedLineItems, setEditedLineItems] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hasPackingSlipDependency, setHasPackingSlipDependency] = useState(false);
  const [packingSlips, setPackingSlips] = useState([]);
  /** Applied only to the hidden print DOM; reset after each print. Default: hide comments and description. */
  const [printLayoutHideComments, setPrintLayoutHideComments] = useState(true);
  const [printLayoutHideDescription, setPrintLayoutHideDescription] = useState(true);
  /** When true (print dialog options only), line items render as two tables: parts vs labor/services. */
  const [printLayoutSeparateLineSections, setPrintLayoutSeparateLineSections] = useState(false);
  const [printSettingsDraft, setPrintSettingsDraft] = useState({
    hideComments: true,
    hideDescription: true,
    separateLineSections: false
  });
  const [orderPrintSettingsOpen, setOrderPrintSettingsOpen] = useState(false);
  const [copyOrderDialogOpen, setCopyOrderDialogOpen] = useState(false);
  const [copyOrderBusy, setCopyOrderBusy] = useState(false);
  const [lineageData, setLineageData] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(true);
  const [lineageError, setLineageError] = useState('');
  const [orderInvoices, setOrderInvoices] = useState([]);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [loadingDeleteDependencies, setLoadingDeleteDependencies] = useState(false);
  const [hasCustomerPayments, setHasCustomerPayments] = useState(false);
  const [deleteLineItemDialogOpen, setDeleteLineItemDialogOpen] = useState(false);
  const [lineItemToDelete, setLineItemToDelete] = useState(null);
  const [editedOrder, setEditedOrder] = useState(null);
  const editing = !!editedOrder;
  const [orderCustomerAddresses, setOrderCustomerAddresses] = useState([]);
  const [orderAddressesModalOpen, setOrderAddressesModalOpen] = useState(false);
  const [editLockBusy, setEditLockBusy] = useState(false);
  const holdEditLockRef = useRef(false);
  const [returnToStockOpen, setReturnToStockOpen] = useState(false);
  const [returnToStockItem, setReturnToStockItem] = useState(null);
  const [returnToStockQty, setReturnToStockQty] = useState('');
  const [returnToStockReason, setReturnToStockReason] = useState('');
  const [returnToStockSaving, setReturnToStockSaving] = useState(false);
  const [newPartModalOpen, setNewPartModalOpen] = useState(false);
  const [newPartModalRowIndex, setNewPartModalRowIndex] = useState(null);
  const [newPartModalItemId, setNewPartModalItemId] = useState(null);
  const [newServiceModalOpen, setNewServiceModalOpen] = useState(false);
  const [newServiceModalRowIndex, setNewServiceModalRowIndex] = useState(null);
  const [newServiceModalItemId, setNewServiceModalItemId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [invoiceDraftOpen, setInvoiceDraftOpen] = useState(false);
  const [invoiceDraftLoading, setInvoiceDraftLoading] = useState(false);
  const [invoiceDraftSubmitting, setInvoiceDraftSubmitting] = useState(false);
  const [invoiceDraftExisting, setInvoiceDraftExisting] = useState([]);
  const [invoiceDraftLines, setInvoiceDraftLines] = useState([]);
  const [invoiceDraftShipping, setInvoiceDraftShipping] = useState('');
  const [invoiceDraftTaxPct, setInvoiceDraftTaxPct] = useState('');
  const [invoiceDraftTaxAmt, setInvoiceDraftTaxAmt] = useState('');
  const [invoiceDraftKind, setInvoiceDraftKind] = useState('line_items');
  const [invoiceDraftScheduleRows, setInvoiceDraftScheduleRows] = useState([]);
  const [invoiceDraftCapUninvoiced, setInvoiceDraftCapUninvoiced] = useState(null);
  const [invoiceDraftOrderTotal, setInvoiceDraftOrderTotal] = useState(null);
  const [invoiceDraftSchedulePercentDrafts, setInvoiceDraftSchedulePercentDrafts] = useState({});
  const [lineTextPopoutOpen, setLineTextPopoutOpen] = useState(false);
  const [lineTextPopoutTitle, setLineTextPopoutTitle] = useState('');
  const [lineTextPopoutValue, setLineTextPopoutValue] = useState('');
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionDialogItems, setRevisionDialogItems] = useState([]);
  const [revisionDialogLoading, setRevisionDialogLoading] = useState(false);
  const [procurementSummary, setProcurementSummary] = useState(null);
  const [completionReadiness, setCompletionReadiness] = useState(null);
  const [orderTaxDraft, setOrderTaxDraft] = useState({ mode: 'none', percentStr: '', fixedStr: '' });
  const [orderDiscountDraft, setOrderDiscountDraft] = useState({
    mode: 'none',
    percentStr: '',
    fixedStr: '',
    note: ''
  });
  const [discountInvoiceWarnOpen, setDiscountInvoiceWarnOpen] = useState(false);
  const hasAutoPrintedRef = useRef(false);
  const thirdPartySubcontractRef = useRef(null);
  const orderPrintFilename = order?.identifier || order?.work_order_number || 'Work-Order';
  /** Blob URLs for Shop-category attachments; non-null only while shop-docs print payload is active. */
  const [shopDocPrintAssets, setShopDocPrintAssets] = useState(null);
  const [shopDocPrintBusy, setShopDocPrintBusy] = useState(false);

  const isTmOrder = (o) => (o?.order_type || '').toUpperCase() === 'TIME_AND_MATERIALS';
  const isScopeOfWorkOrder = (o) => (o?.order_style || '').toLowerCase() === 'scope_of_work';
  const isHistoricalOrder = !!order?.superseded_by_work_order_id;
  const orderLockedByOther =
    !!order?.edit_lock &&
    user != null &&
    Number(order.edit_lock.employee_id) !== Number(user.id);
  const canEditLineItems = editing && !isHistoricalOrder;
  const formatModifiedTimestamp = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    // Example target: "26/03/26 2pm"
    return format(d, 'dd/MM/yy h:mma').replace(':00', '').toLowerCase();
  };
  /** T&M or Field Work / Service; standard line-item orders leave Type blank in the UI. */
  const orderTypeLabel = (o) => {
    if (isTmOrder(o)) return 'T&M';
    if (isScopeOfWorkOrder(o)) return 'Field Work / Service';
    return '';
  };

  /** Tab indices: Field Work orders omit the Line Items tab (shift higher tabs down by one). */
  const orderDetailTabs = useMemo(() => {
    if (order && isScopeOfWorkOrder(order)) {
      return {
        general: 0,
        workHrs: 1,
        shipping: 2,
        addresses: 3,
        comments: 4,
        shipments: 5,
        invoices: 6,
        lineage: 7,
        attachments: 8
      };
    }
    return {
      general: 0,
      workHrs: 1,
      lineItems: 2,
      shipping: 3,
      addresses: 4,
      comments: 5,
      shipments: 6,
      invoices: 7,
      lineage: 8,
      attachments: 9
    };
  }, [order]);

  const orderTabRemapPrevRef = useRef({ orderId: null, isFw: false });
  const invoiceOpenFromQueryHandledRef = useRef(false);
  useEffect(() => {
    if (!order) return;
    const oid = order.id;
    const isFw = isScopeOfWorkOrder(order);
    const prev = orderTabRemapPrevRef.current;
    if (prev.orderId === oid) return;
    const prevFw = prev.orderId != null ? prev.isFw : null;
    orderTabRemapPrevRef.current = { orderId: oid, isFw };
    if (prev.orderId == null) {
      if (isFw) {
        setActiveTab((t) => {
          if (t === 2) return 1;
          if (t > 2) return t - 1;
          return t;
        });
      }
      return;
    }
    if (isFw && !prevFw) {
      setActiveTab((t) => {
        if (t === 2) return 1;
        if (t > 2) return t - 1;
        return t;
      });
    } else if (!isFw && prevFw) {
      setActiveTab((t) => {
        if (t >= 2) return t + 1;
        return t;
      });
    }
  }, [order?.id, order?.order_style]);

  useEffect(() => {
    invoiceOpenFromQueryHandledRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!order || loading) return;
    if (searchParams.get('tab') !== 'invoices') return;
    if (orderDetailTabs.invoices == null) return;
    setActiveTab(orderDetailTabs.invoices);
  }, [order, loading, searchParams, orderDetailTabs]);

  useEffect(() => {
    if (!order || loading) return;
    if (searchParams.get('tab') !== 'work-hrs') return;
    if (orderDetailTabs.workHrs == null) return;
    setActiveTab(orderDetailTabs.workHrs);
  }, [order, loading, searchParams, orderDetailTabs]);

  const tmChipSx = { fontWeight: 600, bgcolor: '#ffebee', color: '#c62828', '& .MuiChip-label': { px: 1 } };
  const scopeOfWorkChipSx = { fontWeight: 600, bgcolor: '#e3f2fd', color: '#1565c0', '& .MuiChip-label': { px: 1 } };
  useEffect(() => {
    loadOrder();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLineageLoading(true);
    setLineageError('');
    orderService
      .getLineage(id)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) setLineageData(res.data);
        else setLineageData(null);
      })
      .catch(() => {
        if (!cancelled) setLineageError('Could not load lineage.');
      })
      .finally(() => {
        if (!cancelled) setLineageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // If ?print=1 was requested, automatically print once the order is loaded.
  useEffect(() => {
    if (!printRequested) return;
    if (loading) return;
    if (!order) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    document.body.classList.remove('wm-print-shop-docs-only');
    flushSync(() => {
      setPrintLayoutHideComments(true);
      setPrintLayoutHideDescription(true);
      setPrintLayoutSeparateLineSections(false);
    });
    printWithFilename(orderPrintFilename, { delayMs: 500 });
  }, [printRequested, loading, order, orderPrintFilename]);

  useEffect(() => {
    if (!printRequested) {
      hasAutoPrintedRef.current = false;
    }
  }, [printRequested]);

  useEffect(() => {
    if (id) {
      loadAttachments();
    }
  }, [id]);

  const refreshOrderDependencies = async (workOrderPk) => {
    const rawWo = workOrderPk != null && workOrderPk !== '' ? workOrderPk : id;
    if (rawWo == null || rawWo === '') return;
    const wo = Number(rawWo);
    if (!Number.isFinite(wo) || wo <= 0) return;
    try {
      setLoadingDeleteDependencies(true);
      const [slipsRes, invRes, payRes, procRes, compRes] = await Promise.all([
        packingSlipService.getAll({ work_order_id: wo, limit: 100, offset: 0 }).catch((e) => {
          console.warn('packingSlipService.getAll failed (order dependencies):', e?.message || e);
          return null;
        }),
        customerInvoiceService.getAll({ work_order_id: wo, limit: 100, offset: 0 }).catch((e) => {
          console.warn('customerInvoiceService.getAll failed (order dependencies):', e?.message || e);
          return null;
        }),
        customerPaymentService.getAll({ work_order_id: wo, limit: 5, offset: 0 }).catch(() => ({ data: [] })),
        orderService.getProcurementSummary(wo).catch((e) => {
          console.warn('getProcurementSummary failed:', e?.message || e);
          return null;
        }),
        orderService.getCompletionReadiness(wo).catch(() => null)
      ]);
      const slips = slipsRes
        ? Array.isArray(slipsRes?.data)
          ? slipsRes.data
          : (slipsRes?.data?.data ?? [])
        : [];
      setHasPackingSlipDependency(slips.length > 0);
      setPackingSlips(slips);
      const invList = invRes
        ? Array.isArray(invRes?.data)
          ? invRes.data
          : Array.isArray(invRes?.data?.data)
            ? invRes.data.data
            : []
        : [];
      setOrderInvoices(invList);
      const payList = Array.isArray(payRes?.data) ? payRes.data : [];
      setHasCustomerPayments(payList.length > 0);
      const procPayload = procRes?.data ?? procRes ?? null;
      setProcurementSummary(procPayload && typeof procPayload === 'object' ? procPayload : null);
      const compPayload = compRes?.data ?? compRes ?? null;
      setCompletionReadiness(compPayload && typeof compPayload === 'object' ? compPayload : null);
    } catch (err) {
      console.error('Error loading order dependencies:', err);
      setHasPackingSlipDependency(false);
      setPackingSlips([]);
      setOrderInvoices([]);
      setHasCustomerPayments(false);
      setProcurementSummary(null);
      setCompletionReadiness(null);
    } finally {
      setLoadingDeleteDependencies(false);
    }
  };

  const getDeleteOrderDialogBody = () => {
    const label = order?.identifier || order?.work_order_number || '';
    if (loadingDeleteDependencies) {
      return 'Checking whether this order can be deleted…';
    }
    const hasInv = (orderInvoices?.length > 0) || !!order?.customer_invoice_id;
    const hasPay = hasCustomerPayments;
    const hasSlip = hasPackingSlipDependency;
    if (!hasSlip && !hasInv && !hasPay) {
      return `Are you sure you want to delete Order #${label}? This action cannot be undone.`;
    }
    if (hasPay && !hasSlip && !hasInv) {
      return 'This order has customer payments recorded against it. It cannot be deleted until those payments are removed in Customer Payments (or otherwise cleared).';
    }
    const parts = [];
    if (hasSlip) parts.push('packing slips');
    if (hasInv) parts.push('invoice(s)');
    if (hasPay) parts.push('customer payments');
    const list =
      parts.length === 1
        ? parts[0]
        : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    let msg = `Order #${label} cannot be deleted because it has ${list}.`;
    if (hasPay) {
      msg += '\n\nThis order has customer payments recorded against it. It cannot be deleted until those payments are removed in Customer Payments (or otherwise cleared).';
    }
    return msg;
  };

  const deleteOrderConfirmDisabled =
    loadingDeleteDependencies ||
    hasPackingSlipDependency ||
    orderInvoices.length > 0 ||
    !!order?.customer_invoice_id ||
    hasCustomerPayments;

  useEffect(() => {
    if (!order) return;
    setOrderTaxDraft({
      mode: normalizeOrderTaxModeUi(order.tax_mode),
      percentStr:
        order.tax_percent != null && order.tax_percent !== ''
          ? formatTaxPercentDisplayValue(order.tax_percent)
          : '',
      fixedStr: order.tax_fixed_amount != null && order.tax_fixed_amount !== '' ? String(order.tax_fixed_amount) : ''
    });
  }, [order?.id, order?.tax_mode, order?.tax_percent, order?.tax_fixed_amount]);

  useEffect(() => {
    if (!order) return;
    setOrderDiscountDraft({
      mode: normalizeOrderDiscountModeUi(order.discount_mode),
      percentStr:
        order.discount_percent != null && order.discount_percent !== ''
          ? formatTaxPercentDisplayValue(order.discount_percent)
          : '',
      fixedStr:
        order.discount_fixed_amount != null && order.discount_fixed_amount !== ''
          ? String(order.discount_fixed_amount)
          : '',
      note: order.discount_note != null ? String(order.discount_note) : ''
    });
  }, [
    order?.id,
    order?.discount_mode,
    order?.discount_percent,
    order?.discount_fixed_amount,
    order?.discount_note
  ]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await orderService.getById(id);
      const orderData = response?.data ?? response;
      setOrder(orderData);
      try {
        setRevisionDialogLoading(true);
        const revRes = await orderService.getRevisions(id);
        const revPayload = revRes?.data ? revRes.data : revRes;
        const revRows = Array.isArray(revPayload?.data)
          ? revPayload.data
          : (Array.isArray(revPayload) ? revPayload : []);
        setRevisionDialogItems(revRows);
      } catch (revErr) {
        console.error('Error loading order revisions:', revErr);
        setRevisionDialogItems([]);
      } finally {
        setRevisionDialogLoading(false);
      }
      await refreshOrderDependencies(orderData?.id ?? id);
    } catch (err) {
      console.error('Error loading order:', err);
      setError(err.response?.data?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cid = order?.customer_id;
    if (!cid) {
      setOrderCustomerAddresses([]);
      return undefined;
    }
    let cancelled = false;
    addressService
      .getByCustomerId(cid)
      .then((res) => {
        if (!cancelled) setOrderCustomerAddresses(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setOrderCustomerAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.customer_id]);

  const releaseHeldEditLock = async () => {
    if (!holdEditLockRef.current || !id) return;
    holdEditLockRef.current = false;
    try {
      await orderService.releaseEditLock(id);
      setOrder((o) => (o ? { ...o, edit_lock: null } : o));
    } catch {
      // Stale locks expire server-side
    }
  };

  const handleForceClearEditLock = async () => {
    if (!id || !isAdmin()) return;
    setError('');
    try {
      await orderService.releaseEditLock(id, { force: true });
      setOrder((o) => (o ? { ...o, edit_lock: null } : o));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to clear edit lock');
    }
  };

  useEffect(() => {
    if (!editing || !id) return undefined;
    let cancelled = false;
    const beat = async () => {
      if (cancelled) return;
      try {
        const r = await orderService.refreshEditLock(id);
        const el = r?.data?.edit_lock;
        if (el && !cancelled) setOrder((prev) => (prev ? { ...prev, edit_lock: el } : prev));
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 409) {
          holdEditLockRef.current = false;
          setEditedOrder(null);
          setEditingItemId(null);
          setEditedLineItems({});
          const el = err.response?.data?.data?.edit_lock;
          setOrder((prev) => (prev ? { ...prev, edit_lock: el ?? null } : prev));
          setError(
            err.response?.data?.message ||
              'Your edit session ended (lock cleared, expired, or taken by someone else). Your unsaved changes were discarded.'
          );
        }
      }
    };
    beat();
    const t = setInterval(beat, EDIT_LOCK_HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [editing, id]);

  useEffect(() => {
    if (!id || editing) return undefined;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await orderService.getEditLock(id);
        const el = r?.data?.edit_lock ?? null;
        if (!cancelled) {
          setOrder((prev) =>
            prev && String(prev.id) === String(id) ? { ...prev, edit_lock: el } : prev
          );
        }
      } catch {
        /* ignore */
      }
    };
    poll();
    const t = setInterval(poll, EDIT_LOCK_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [id, editing]);

  useEffect(() => {
    const wid = id;
    return () => {
      if (holdEditLockRef.current && wid) {
        holdEditLockRef.current = false;
        orderService.releaseEditLock(wid).catch(() => {});
      }
    };
  }, [id]);

  const saveOrderTax = async (nextDraft, opts = {}) => {
    if (isHistoricalOrder || !id || !order) return;
    const draft = nextDraft || orderTaxDraft;
    try {
      setSaving(true);
      setError('');
      const mode = normalizeOrderTaxModeUi(draft.mode);
      let tax_percent = null;
      let tax_fixed_amount = null;
      if (mode === 'percent') {
        const raw = String(draft.percentStr ?? '').trim();
        const t = raw === '' ? '' : formatTaxPercentDisplayValue(raw);
        if (t === '') tax_percent = null;
        else {
          const p = parseFloat(t);
          if (!Number.isFinite(p) || p < 0) {
            setError('Tax percent must be a non-negative number');
            return;
          }
          tax_percent = Math.round(p * 100) / 100;
        }
      } else if (mode === 'fixed') {
        const t = String(draft.fixedStr ?? '').trim();
        if (t === '') tax_fixed_amount = null;
        else {
          const f = parseFloat(t);
          if (!Number.isFinite(f) || f < 0) {
            setError('Tax amount must be a non-negative number');
            return;
          }
          tax_fixed_amount = Math.round(f * 100) / 100;
        }
      }
      const tax_basis = normalizeTaxBasisUi(
        opts.tax_basis !== undefined && opts.tax_basis !== null ? opts.tax_basis : order.tax_basis
      );
      const res = await orderService.updateTax(id, { tax_mode: mode, tax_percent, tax_fixed_amount, tax_basis });
      const nextOrder = res?.data;
      if (nextOrder && typeof nextOrder === 'object') {
        setOrder((prev) => ({ ...(prev && typeof prev === 'object' ? prev : {}), ...nextOrder }));
      } else {
        await refreshAfterOrderMutation();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save tax');
    } finally {
      setSaving(false);
    }
  };

  const unpaidNonVoidedInvoices = useMemo(
    () =>
      (orderInvoices || []).filter((inv) => {
        if (inv.voided_at) return false;
        if (inv.paid_at) return false;
        return parseFloat(inv.amount_paid || 0) <= 0.005;
      }),
    [orderInvoices]
  );

  const saveOrderDiscount = async (nextDraft, { skipWarn } = {}) => {
    if (isHistoricalOrder || !id || !order) return;
    const draft = nextDraft || orderDiscountDraft;
    if (!skipWarn && unpaidNonVoidedInvoices.length > 0) {
      setDiscountInvoiceWarnOpen(true);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const mode = normalizeOrderDiscountModeUi(draft.mode);
      let discount_percent = null;
      let discount_fixed_amount = null;
      if (mode === 'percent') {
        const raw = String(draft.percentStr ?? '').trim();
        const t = raw === '' ? '' : formatTaxPercentDisplayValue(raw);
        if (t === '') discount_percent = null;
        else {
          const p = parseFloat(t);
          if (!Number.isFinite(p) || p < 0) {
            setError('Discount percent must be a non-negative number');
            return;
          }
          discount_percent = Math.round(p * 100) / 100;
        }
      } else if (mode === 'amount') {
        const t = String(draft.fixedStr ?? '')
          .trim()
          .replace(/[$,\s]/g, '');
        if (t === '') discount_fixed_amount = null;
        else {
          const f = parseFloat(t);
          if (!Number.isFinite(f) || f < 0) {
            setError('Discount amount must be a non-negative number');
            return;
          }
          discount_fixed_amount = Math.round(f * 100) / 100;
        }
      }
      const res = await orderService.updateDiscount(id, {
        discount_mode: mode,
        discount_percent,
        discount_fixed_amount,
        discount_note: String(draft.note || '').trim() || null
      });
      const nextOrder = res?.data;
      if (nextOrder && typeof nextOrder === 'object') {
        setOrder((prev) => ({ ...(prev && typeof prev === 'object' ? prev : {}), ...nextOrder }));
      } else {
        await refreshAfterOrderMutation();
      }
      setDiscountInvoiceWarnOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };

  const applyOrderTaxScopePreset = async (preset) => {
    if (preset == null || isHistoricalOrder || !id || !order || saving) return;
    try {
      setSaving(true);
      setError('');
      let res;
      if (preset === 'line_items') {
        res = await orderService.updateTax(id, {
          tax_basis: 'per_line',
          tax_mode: 'none',
          tax_percent: null,
          tax_fixed_amount: null
        });
      } else if (preset === 'no_tax') {
        res = await orderService.updateTax(id, {
          tax_basis: 'document',
          tax_mode: 'none',
          tax_percent: null,
          tax_fixed_amount: null
        });
      } else {
        const currentMode = normalizeOrderTaxModeUi(order.tax_mode);
        const nextMode = currentMode === 'none' ? 'percent' : currentMode;
        const payload = { tax_basis: 'document', tax_mode: nextMode };
        if (nextMode === 'percent') {
          payload.tax_percent =
            order.tax_percent != null && order.tax_percent !== '' ? Number(order.tax_percent) : null;
          payload.tax_fixed_amount = null;
        } else {
          payload.tax_fixed_amount =
            order.tax_fixed_amount != null && order.tax_fixed_amount !== ''
              ? Number(order.tax_fixed_amount)
              : null;
          payload.tax_percent = null;
        }
        res = await orderService.updateTax(id, payload);
      }
      const nextOrder = res?.data;
      if (nextOrder && typeof nextOrder === 'object') {
        setOrder((prev) => ({ ...(prev && typeof prev === 'object' ? prev : {}), ...nextOrder }));
      }
    } catch (err) {
      console.error('Error saving order tax scope:', err);
      setError(err.response?.data?.message || 'Failed to update tax');
    } finally {
      setSaving(false);
    }
  };

  /** After persisting order changes: refresh order + line items + totals without main loading spinner. */
  const refreshAfterOrderMutation = async () => {
    let refreshedWoId = id;
    try {
      const response = await orderService.getById(id);
      const orderData = response?.data ?? response;
      refreshedWoId = orderData?.id ?? id;
      setOrder(orderData);
    } catch (err) {
      console.error('Error refreshing order:', err);
      setError(err.response?.data?.message || 'Failed to refresh order');
    }
    await refreshOrderDependencies(refreshedWoId);
  };

  const loadAttachments = async () => {
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForWorkOrder(id);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      console.error('Error loading attachments:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (!files || files.length === 0) return;

    // Enforce max 5 files per batch
    if (files.length > 5) {
      setAttachmentsError('You can upload a maximum of 5 files at a time.');
      return;
    }

    const tooLarge = files.filter((f) => f.size > MAX_ATTACHMENT_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      setAttachmentsError(`One or more files exceed the ${MAX_ATTACHMENT_FILE_SIZE_LABEL} size limit.`);
      return;
    }

    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, workOrderId: id });
      }
      await loadAttachments();
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleFileInputChange = async (event) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
    event.target.value = '';
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    setIsDraggingOverAttachment(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDraggingOverAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setAttachmentsError('');
      await attachmentService.delete(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      if (!attachment?.id) return;
      setAttachmentsError('');
      const res = await attachmentService.download(attachment.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name || `attachment-${attachment.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  // Prevent browser from opening files when dropped outside the drop zone (e.g. on Mac)
  useEffect(() => {
    if (activeTab !== orderDetailTabs.attachments) return;
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, [activeTab, orderDetailTabs.attachments]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleReturnToStock = async () => {
    if (!returnToStockItem?.id) return;
    const qty = parseFloat(returnToStockQty);
    if (isNaN(qty) || qty <= 0 || qty > (returnToStockItem.quantity_used || 0)) {
      setError('Enter a valid quantity (positive, not more than issued).');
      return;
    }
    setReturnToStockSaving(true);
    setError('');
    try {
      await inventoryService.returnToStock(returnToStockItem.id, qty, returnToStockReason);
      setReturnToStockOpen(false);
      setReturnToStockItem(null);
      setReturnToStockQty('');
      setReturnToStockReason('');
      await loadOrder();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to return to stock');
    } finally {
      setReturnToStockSaving(false);
    }
  };

  const handleEditOrder = async () => {
    if (isHistoricalOrder) {
      setError('This order is a historical revision and is read-only. View the next version instead.');
      return;
    }
    if (orderLockedByOther) return;
    setError('');
    setEditLockBusy(true);
    try {
      await orderService.acquireEditLock(id);
      holdEditLockRef.current = true;
      setEditedOrder({
        customer_id: order.customer_id ?? null,
        employee_id: order.employee_id ?? null,
        salesman_id: order.salesman_id ?? null,
        status: order.status || 'open',
        description: order.description || '',
        notes: order.notes || '',
        po_number: order.po_number || '',
        currency_code: order.currency_code || 'USD',
        order_date: formatDateForInput(order.order_date),
        due_date: formatDateForInput(order.due_date),
        billing_customer_address_id:
          order.billing_customer_address_id != null ? String(order.billing_customer_address_id) : '',
        shipping_customer_address_id:
          order.shipping_customer_address_id != null ? String(order.shipping_customer_address_id) : '',
        shipping_manual_line1: order.shipping_manual_line1 ?? '',
        shipping_manual_line2: order.shipping_manual_line2 ?? '',
        shipping_manual_city: order.shipping_manual_city ?? '',
        shipping_manual_state: order.shipping_manual_state ?? '',
        shipping_manual_zip: order.shipping_manual_zip ?? '',
        shipping_manual_country: order.shipping_manual_country ?? '',
        ...(isScopeOfWorkOrder(order) ? fieldWorkFieldsFromOrder(order) : {})
      });
      const refreshLock = await orderService.getEditLock(id);
      const el = refreshLock?.data?.edit_lock;
      if (el) setOrder((o) => (o ? { ...o, edit_lock: el } : o));
    } catch (err) {
      if (err.response?.status === 409) {
        const el = err.response?.data?.data?.edit_lock;
        if (el) setOrder((o) => (o ? { ...o, edit_lock: el } : o));
        setError(err.response?.data?.message || 'Another user is editing this order.');
      } else {
        setError(err.response?.data?.message || 'Could not start editing this order.');
      }
    } finally {
      setEditLockBusy(false);
    }
  };

  const handleCancelOrderEdit = async () => {
    if (saving) return;
    await releaseHeldEditLock();
    setEditedOrder(null);
    setEditingItemId(null);
    setEditedLineItems({});
  };

  const handleSaveOrderEdit = async () => {
    if (!id || !order || !editedOrder) return;
    try {
      setSaving(true);
      setError('');
      if (newLineItems.length > 0) {
        for (const item of newLineItems) {
          const type = item.item_type === 'service' ? 'service' : 'part';
          const partId = type === 'part' ? (item.part_id ? parseInt(item.part_id, 10) : null) : null;
          const serviceId = type === 'service' ? (item.service_id ? parseInt(item.service_id, 10) : null) : null;
          if ((type === 'part' && !partId) || (type === 'service' && !serviceId)) {
            const msg = 'Each new line item must select a part or service before saving the order.';
            setError(msg);
            throw new Error(msg);
          }
        }
      }
      const normalizeDateInput = (value) => {
        if (value == null) return undefined;
        const s = String(value).trim();
        return s === '' ? undefined : s;
      };
      const normalizedOrderDate =
        normalizeDateInput(editedOrder.order_date) ?? normalizeDateInput(formatDateForInput(order.order_date));
      const normalizedDueDate =
        normalizeDateInput(editedOrder.due_date) ?? normalizeDateInput(formatDateForInput(order.due_date));
      await orderService.update(id, {
        customer_id: editedOrder.customer_id ?? order.customer_id ?? null,
        employee_id: editedOrder.employee_id ?? order.employee_id ?? null,
        salesman_id: editedOrder.salesman_id ?? order.salesman_id ?? null,
        status: editedOrder.status || order.status || 'open',
        description: editedOrder.description ?? '',
        notes: editedOrder.notes ?? '',
        po_number: editedOrder.po_number ?? '',
        currency_code: editedOrder.currency_code || order.currency_code || 'USD',
        ...(normalizedOrderDate !== undefined ? { order_date: normalizedOrderDate } : {}),
        ...(normalizedDueDate !== undefined ? { due_date: normalizedDueDate } : {}),
        billing_customer_address_id:
          editedOrder.billing_customer_address_id !== '' && editedOrder.billing_customer_address_id != null
            ? parseInt(editedOrder.billing_customer_address_id, 10)
            : null,
        shipping_customer_address_id:
          editedOrder.shipping_customer_address_id !== '' && editedOrder.shipping_customer_address_id != null
            ? parseInt(editedOrder.shipping_customer_address_id, 10)
            : null,
        shipping_manual_line1: editedOrder.shipping_manual_line1,
        shipping_manual_line2: editedOrder.shipping_manual_line2,
        shipping_manual_city: editedOrder.shipping_manual_city,
        shipping_manual_state: editedOrder.shipping_manual_state,
        shipping_manual_zip: editedOrder.shipping_manual_zip,
        shipping_manual_country: editedOrder.shipping_manual_country,
        ...(isScopeOfWorkOrder(order)
          ? {
              sow_scope_sections: (editedOrder.sow_scope_sections || [])
                .map((s) => String(s || '').trim())
                .filter(Boolean),
              sow_clarifications_sections: (editedOrder.sow_clarifications_sections || [])
                .map((s) => String(s || '').trim())
                .filter(Boolean),
              proposal_materials_amount: parseFloat(editedOrder.proposal_materials_amount) || 0,
              proposal_labor_amount: parseFloat(editedOrder.proposal_labor_amount) || 0,
              proposal_rentals_amount: parseFloat(editedOrder.proposal_rentals_amount) || 0,
              proposal_total_amount: resolveProposalTotalAmount({
                proposal_total_amount: editedOrder.proposal_total_amount,
                proposal_materials_amount: editedOrder.proposal_materials_amount,
                proposal_labor_amount: editedOrder.proposal_labor_amount,
                proposal_rentals_amount: editedOrder.proposal_rentals_amount
              }),
              proposal_show_breakdown: !!editedOrder.proposal_show_breakdown,
              sow_list_style: editedOrder.sow_list_style || 'bullets'
            }
          : {})
      });
      for (const item of newLineItems) {
        const type = item.item_type === 'service' ? 'service' : 'part';
        const partId = type === 'part' ? (item.part_id ? parseInt(item.part_id, 10) : null) : null;
        const serviceId = type === 'service' ? (item.service_id ? parseInt(item.service_id, 10) : null) : null;
        const qtyRaw = parseFloat(item.quantity_required) || 0;
        const quantity_required =
          type === 'service' ? Math.round(qtyRaw * 100) / 100 : qtyRaw;
        await orderService.addLineItem(id, {
          item_type: type,
          part_id: partId,
          service_id: serviceId,
          quantity_required,
          quantity_used: parseFloat(item.quantity_used) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          unit: item.unit || (type === 'service' ? 'HR' : 'EA'),
          description: item.description || '',
          notes: item.notes || '',
          qty_open: parseFloat(item.qty_open) || 0,
          ...buildWorkOrderLineItemTaxPayload(item)
        });
      }
      if (newLineItems.length > 0) {
        setNewLineItems([]);
      }
      await releaseHeldEditLock();
      await refreshAfterOrderMutation();
      setEditedOrder(null);
      setEditingItemId(null);
      setEditedLineItems({});
    } catch (err) {
      console.error('Error saving order:', err);
      setError(err.response?.data?.message || 'Failed to save order');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleOrderFieldChange = (field) => (e) => {
    setEditedOrder({
      ...editedOrder,
      [field]: e.target.value
    });
  };

  const editedOrderShippingSelectValue = useMemo(() => {
    if (!editedOrder) return '';
    if (String(editedOrder.shipping_manual_line1 || '').trim() !== '') return 'custom';
    return editedOrder.shipping_customer_address_id ? String(editedOrder.shipping_customer_address_id) : '';
  }, [editedOrder]);

  const handleOrderBillingAddressSelect = (e) => {
    if (!editedOrder) return;
    setEditedOrder({ ...editedOrder, billing_customer_address_id: e.target.value });
  };

  const handleOrderShippingSelect = (e) => {
    if (!editedOrder) return;
    const v = e.target.value;
    if (v === '') {
      setEditedOrder({
        ...editedOrder,
        shipping_customer_address_id: '',
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      });
    } else if (v === 'custom') {
      setEditedOrder({ ...editedOrder, shipping_customer_address_id: '' });
    } else {
      setEditedOrder({
        ...editedOrder,
        shipping_customer_address_id: v,
        shipping_manual_line1: '',
        shipping_manual_line2: '',
        shipping_manual_city: '',
        shipping_manual_state: '',
        shipping_manual_zip: '',
        shipping_manual_country: ''
      });
    }
  };

  const handleDeleteOrder = () => {
    if (isHistoricalOrder) {
      setError('This order is a historical revision and cannot be deleted.');
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    try {
      setError('');
      await orderService.delete(id);
      navigate('/work-orders');
    } catch (err) {
      console.error('Error deleting order:', err);
      setError(err.response?.data?.message || 'Failed to delete order');
      setDeleteDialogOpen(false);
    }
  };

  const closeInvoiceDraft = () => {
    setInvoiceDraftOpen(false);
    setInvoiceDraftExisting([]);
    setInvoiceDraftLines([]);
    setInvoiceDraftShipping('');
    setInvoiceDraftTaxPct('');
    setInvoiceDraftTaxAmt('');
    setInvoiceDraftKind('line_items');
    setInvoiceDraftScheduleRows([]);
    setInvoiceDraftCapUninvoiced(null);
    setInvoiceDraftOrderTotal(null);
    setInvoiceDraftSchedulePercentDrafts({});
  };

  const openCreateInvoiceDraft = useCallback(async () => {
    if (!id) return;
    try {
      setInvoiceDraftLoading(true);
      setError('');
      const res = await customerInvoiceService.getPartialInvoiceDraft(id);
      const d = res?.data;
      const cap =
        d?.uninvoiced_subtotal_amount != null && Number.isFinite(Number(d.uninvoiced_subtotal_amount))
          ? Number(d.uninvoiced_subtotal_amount)
          : d?.uninvoiced_amount != null && Number.isFinite(Number(d.uninvoiced_amount))
            ? Number(d.uninvoiced_amount)
            : null;
      const orderTotal =
        d?.order_total_amount != null && Number.isFinite(Number(d.order_total_amount))
          ? Number(d.order_total_amount)
          : null;
      const hasDraftLines = Array.isArray(d?.draft_lines) && d.draft_lines.length > 0;
      if (!hasDraftLines && (cap == null || cap <= 0.02)) {
        setError(
          'Nothing left to invoice on this order (order sell total is fully covered by existing invoice subtotals).'
        );
        return;
      }
      setInvoiceDraftExisting(Array.isArray(d.existing_invoices) ? d.existing_invoices : []);
      setInvoiceDraftCapUninvoiced(cap);
      setInvoiceDraftOrderTotal(orderTotal);
      const sortedDraftLines = [...(Array.isArray(d.draft_lines) ? d.draft_lines : [])]
        .map((line) => {
          const p = Math.max(0, parseFloat(line?.unit_price) || 0);
          return {
            ...line,
            unit_price: p.toFixed(2)
          };
        })
        .sort((a, b) => {
          const aSvc = a?.item_type === 'service' ? 1 : 0;
          const bSvc = b?.item_type === 'service' ? 1 : 0;
          if (aSvc !== bSvc) return aSvc - bSvc;
          return (a?.work_order_item_id || 0) - (b?.work_order_item_id || 0);
        });
      setInvoiceDraftLines(JSON.parse(JSON.stringify(sortedDraftLines)));
      setInvoiceDraftKind(hasDraftLines ? 'line_items' : 'payment_schedule');
      const todayYmd = formatDateOnly(new Date(), 'yyyy-MM-dd') || new Date().toISOString().split('T')[0];
      setInvoiceDraftScheduleRows(
        hasDraftLines
          ? []
          : [{ label: '', amount: cap != null ? Math.max(0, cap) : '', due_date: todayYmd, _manualPctPrecision: false }]
      );
      setInvoiceDraftShipping('');
      setInvoiceDraftTaxPct('');
      setInvoiceDraftTaxAmt('');
      setInvoiceDraftSchedulePercentDrafts({});
      setInvoiceDraftOpen(true);
    } catch (err) {
      console.error('Error loading invoice draft:', err);
      setError(err.response?.data?.message || 'Failed to load invoice draft');
    } finally {
      setInvoiceDraftLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!order || loading) return;
    if (searchParams.get('tab') !== 'invoices' || searchParams.get('openInvoice') !== '1') return;
    if (invoiceOpenFromQueryHandledRef.current) return;
    invoiceOpenFromQueryHandledRef.current = true;
    setSearchParams({}, { replace: true });
    const timer = window.setTimeout(() => {
      openCreateInvoiceDraft();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [order, loading, searchParams, setSearchParams, openCreateInvoiceDraft]);

  const handleOpenCreatePackingSlip = () => {
    const workOrderId = order?.id || id;
    if (!workOrderId) return;
    navigate(`/packing-slips/new?work_order_id=${workOrderId}`);
  };

  const updateInvoiceDraftLine = (index, field, value) => {
    setInvoiceDraftLines((prev) => {
      const next = [...prev];
      const row = { ...next[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        let q = Math.max(0, parseFloat(field === 'quantity' ? value : row.quantity) || 0);
        const maxQty = Math.max(0, parseFloat(row.quantity_remaining) || 0);
        q = Math.min(maxQty, q);
        if (field === 'quantity') row.quantity = q;
        const p = Math.max(0, parseFloat(field === 'unit_price' ? value : row.unit_price) || 0);
        row.line_total = Math.round(q * p * 100) / 100;
      }
      next[index] = row;
      return next;
    });
  };

  const bumpInvoiceDraftQty = (index, delta) => {
    setInvoiceDraftLines((prev) => {
      const next = [...prev];
      const row = { ...(next[index] || {}) };
      const maxQty = Math.max(0, parseFloat(row.quantity_remaining) || 0);
      const currentQty = Math.max(0, parseFloat(row.quantity) || 0);
      const bumped = Math.max(0, currentQty + delta);
      const nextQty = Math.min(maxQty, bumped);
      row.quantity = String(Math.round(nextQty * 1000) / 1000);
      const p = Math.max(0, parseFloat(row.unit_price) || 0);
      row.line_total = Math.round(nextQty * p * 100) / 100;
      next[index] = row;
      return next;
    });
  };

  const calculateScheduleRowRemainingAmount = (capAmount, rows = invoiceDraftScheduleRows) => {
    if (capAmount == null || !Number.isFinite(Number(capAmount))) return '';
    const sub = rows.reduce((s, r) => s + Math.max(0, parseFloat(r?.amount) || 0), 0);
    const remaining = Math.round((Number(capAmount) - sub) * 100) / 100;
    return remaining > 0.005 ? remaining : '';
  };

  const updateInvoiceDraftScheduleRow = (index, field, value) => {
    setInvoiceDraftScheduleRows((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), [field]: value };
      return next;
    });
  };

  const getInvoiceDraftScheduleRowKey = (row, index) => String(row?.id ?? `new-${index}`);

  const addInvoiceDraftScheduleRow = () => {
    const todayYmd = formatDateOnly(new Date(), 'yyyy-MM-dd') || new Date().toISOString().split('T')[0];
    setInvoiceDraftScheduleRows((prev) => [
      ...prev,
      {
        label: '',
        amount: calculateScheduleRowRemainingAmount(invoiceDraftCapUninvoiced, prev),
        due_date: todayYmd,
        _manualPctPrecision: false
      }
    ]);
  };

  const removeInvoiceDraftScheduleRow = (index) => {
    setInvoiceDraftScheduleRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const invoiceDraftSubtotal = useMemo(
    () => invoiceDraftLines.reduce((s, r) => s + (parseFloat(r.line_total) || 0), 0),
    [invoiceDraftLines]
  );

  /** Auto header discount so line merchandise fits remaining discounted order sell total (matches server). */
  const invoiceDraftAutoDiscount = useMemo(() => {
    if (invoiceDraftCapUninvoiced == null) return 0;
    const merch = Math.round(invoiceDraftSubtotal * 100) / 100;
    const cap = Math.round(Number(invoiceDraftCapUninvoiced) * 100) / 100;
    if (!(merch > cap + 0.02)) return 0;
    return Math.round(Math.min(merch, merch - cap) * 100) / 100;
  }, [invoiceDraftSubtotal, invoiceDraftCapUninvoiced]);

  const invoiceDraftNetSubtotal = useMemo(
    () => Math.round(Math.max(0, invoiceDraftSubtotal - invoiceDraftAutoDiscount) * 100) / 100,
    [invoiceDraftSubtotal, invoiceDraftAutoDiscount]
  );

  const invoiceDraftLineMoney = useMemo(() => {
    const sub = invoiceDraftNetSubtotal;
    const shipTrim = String(invoiceDraftShipping ?? '').trim();
    const ship = shipTrim === '' ? 0 : Math.max(0, parseFloat(shipTrim) || 0);
    const taxPctTrim = String(invoiceDraftTaxPct ?? '').trim();
    const taxAmtTrim = String(invoiceDraftTaxAmt ?? '').trim();
    let tax = 0;
    if (taxAmtTrim !== '') tax = Math.max(0, parseFloat(taxAmtTrim) || 0);
    else if (taxPctTrim !== '') tax = Math.round(((sub * (parseFloat(taxPctTrim) || 0)) / 100) * 100) / 100;
    const total = Math.round((sub + ship + tax) * 100) / 100;
    return { merchandise: invoiceDraftSubtotal, discount: invoiceDraftAutoDiscount, subtotal: sub, shipping: ship, tax, total };
  }, [
    invoiceDraftSubtotal,
    invoiceDraftAutoDiscount,
    invoiceDraftNetSubtotal,
    invoiceDraftShipping,
    invoiceDraftTaxPct,
    invoiceDraftTaxAmt
  ]);

  const invoiceDraftScheduleMoney = useMemo(() => {
    const merch = invoiceDraftScheduleRows.reduce((s, r) => s + Math.max(0, parseFloat(r.amount) || 0), 0);
    const merchRounded = Math.round(merch * 100) / 100;
    let discount = 0;
    if (invoiceDraftCapUninvoiced != null) {
      const cap = Math.round(Number(invoiceDraftCapUninvoiced) * 100) / 100;
      if (merchRounded > cap + 0.02) {
        discount = Math.round(Math.min(merchRounded, merchRounded - cap) * 100) / 100;
      }
    }
    const sub = Math.round(Math.max(0, merchRounded - discount) * 100) / 100;
    const shipTrim = String(invoiceDraftShipping ?? '').trim();
    const ship = shipTrim === '' ? 0 : Math.max(0, parseFloat(shipTrim) || 0);
    const taxPctTrim = String(invoiceDraftTaxPct ?? '').trim();
    const taxAmtTrim = String(invoiceDraftTaxAmt ?? '').trim();
    let tax = 0;
    if (taxAmtTrim !== '') tax = Math.max(0, parseFloat(taxAmtTrim) || 0);
    else if (taxPctTrim !== '') tax = Math.round(((sub * (parseFloat(taxPctTrim) || 0)) / 100) * 100) / 100;
    const total = Math.round((sub + ship + tax) * 100) / 100;
    return { merchandise: merchRounded, discount, subtotal: sub, shipping: ship, tax, total };
  }, [
    invoiceDraftScheduleRows,
    invoiceDraftCapUninvoiced,
    invoiceDraftShipping,
    invoiceDraftTaxPct,
    invoiceDraftTaxAmt
  ]);

  const existingPaymentScheduleInvoices = useMemo(
    () =>
      (invoiceDraftExisting || []).filter(
        (inv) => String(inv?.invoice_kind || 'line_items') === 'payment_schedule'
      ),
    [invoiceDraftExisting]
  );

  const getInvoiceDraftSchedulePercentDisplay = (row) => {
    const orderTotal = Number(invoiceDraftOrderTotal);
    const amt = Math.max(0, Number(row?.amount) || 0);
    if (!Number.isFinite(orderTotal) || orderTotal <= 0) return '';
    const pct = (amt / orderTotal) * 100;
    if (!Number.isFinite(pct)) return '';
    const roundedWhole = Math.round(pct);
    const isWhole = Math.abs(pct - roundedWhole) < 1e-9;
    if (row?._manualPctPrecision && !isWhole) return (Math.round(pct * 100) / 100).toFixed(2);
    return String(roundedWhole);
  };

  const handleInvoiceDraftScheduleAmountChange = (index, value) => {
    let nextAmount = value === '' ? '' : Number(value);
    if (nextAmount !== '' && !Number.isFinite(nextAmount)) nextAmount = '';
    if (nextAmount !== '' && nextAmount < 0) nextAmount = 0;
    setInvoiceDraftScheduleRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...(next[index] || {}),
        amount: nextAmount === '' ? '' : Math.round(nextAmount * 100) / 100,
        _manualPctPrecision: true
      };
      return next;
    });
  };

  const handleInvoiceDraftSchedulePercentChange = (index, value) => {
    const orderTotal = Number(invoiceDraftOrderTotal);
    if (!Number.isFinite(orderTotal) || orderTotal <= 0) return;
    const pct = Number(value);
    if (!Number.isFinite(pct)) return;
    const bounded = Math.max(0, pct);
    const amount = Math.round(((orderTotal * bounded) / 100) * 100) / 100;
    setInvoiceDraftScheduleRows((prev) => {
      const next = [...prev];
      next[index] = { ...(next[index] || {}), amount, _manualPctPrecision: true };
      return next;
    });
  };

  const handleInvoiceDraftSchedulePercentDraftChange = (index, row, value) => {
    const key = getInvoiceDraftScheduleRowKey(row, index);
    const nextRaw = String(value ?? '');
    if (nextRaw !== '' && !/^\d*\.?\d{0,2}$/.test(nextRaw)) return;
    setInvoiceDraftSchedulePercentDrafts((prev) => ({ ...prev, [key]: nextRaw }));
  };

  const handleInvoiceDraftSchedulePercentDraftFocus = (index, row) => {
    const key = getInvoiceDraftScheduleRowKey(row, index);
    setInvoiceDraftSchedulePercentDrafts((prev) => ({ ...prev, [key]: getInvoiceDraftSchedulePercentDisplay(row) }));
  };

  const handleInvoiceDraftSchedulePercentDraftBlur = (index, row) => {
    const key = getInvoiceDraftScheduleRowKey(row, index);
    const raw = invoiceDraftSchedulePercentDrafts[key];
    if (raw != null && raw !== '') handleInvoiceDraftSchedulePercentChange(index, raw);
    setInvoiceDraftSchedulePercentDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const submitInvoiceDraft = async () => {
    if (!id) return;
    try {
      setInvoiceDraftSubmitting(true);
      setError('');
      const shipTrim = String(invoiceDraftShipping ?? '').trim();
      const taxPctTrim = String(invoiceDraftTaxPct ?? '').trim();
      const taxAmtTrim = String(invoiceDraftTaxAmt ?? '').trim();
      let res;
      if (invoiceDraftKind === 'payment_schedule') {
        const schedule = [];
        for (let i = 0; i < invoiceDraftScheduleRows.length; i++) {
          const r = invoiceDraftScheduleRows[i];
          const label = String(r?.label ?? '').trim();
          const amt = parseFloat(r?.amount);
          const due = String(r?.due_date ?? '').trim().split('T')[0];
          if (!label) {
            setError(`Schedule row ${i + 1}: enter a label.`);
            return;
          }
          if (!Number.isFinite(amt) || amt <= 0) {
            setError(`Schedule row ${i + 1}: enter a valid amount greater than zero.`);
            return;
          }
          if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) {
            setError(`Schedule row ${i + 1}: choose a valid due date.`);
            return;
          }
          schedule.push({ label, amount: amt, due_date: due });
        }
        if (schedule.length === 0) {
          setError('Add at least one schedule row.');
          return;
        }
        const body = { schedule };
        if (shipTrim !== '') body.invoice_shipping_amount = parseFloat(shipTrim);
        if (taxPctTrim !== '') body.invoice_tax_percent = parseFloat(taxPctTrim);
        if (taxAmtTrim !== '') body.invoice_tax_amount = parseFloat(taxAmtTrim);
        res = await customerInvoiceService.createPaymentScheduleFromWorkOrder(id, body);
      } else {
        const payload = {
          lines: invoiceDraftLines.map((row) => ({
            work_order_item_id: row.work_order_item_id,
            quantity: parseFloat(row.quantity) || 0,
            unit_price: parseFloat(row.unit_price) || 0,
            description: row.description
          }))
        };
        if (shipTrim !== '') payload.invoice_shipping_amount = parseFloat(shipTrim);
        if (taxPctTrim !== '') payload.invoice_tax_percent = parseFloat(taxPctTrim);
        if (taxAmtTrim !== '') payload.invoice_tax_amount = parseFloat(taxAmtTrim);
        res = await customerInvoiceService.createFromWorkOrderRemaining(id, payload);
      }
      const inv = res?.data;
      closeInvoiceDraft();
      await loadOrder();
      if (inv?.id) navigate(`/customer-invoices/${inv.id}`);
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setInvoiceDraftSubmitting(false);
    }
  };

  const handleLineItemChange = (index, field, value, catalogRecord = null) => {
    if (field === 'item_type') {
      const nextType = value === 'service' ? 'service' : 'part';
      const updated = [...newLineItems];
      updated[index] = {
        ...updated[index],
        item_type: nextType,
        part_id: nextType === 'part' ? (updated[index].part_id || '') : '',
        service_id: nextType === 'service' ? (updated[index].service_id || '') : '',
        unit: nextType === 'service' ? (updated[index].unit || 'HR') : (updated[index].unit || 'EA')
      };
      setNewLineItems(updated);
      return;
    }
    if (field === 'item_ref' && value === '__new_part__') {
      setNewPartModalRowIndex(index);
      setNewPartModalItemId(null);
      setNewPartModalOpen(true);
      return;
    }
    if (field === 'item_ref' && value === '__new_service__') {
      setNewServiceModalRowIndex(index);
      setNewServiceModalItemId(null);
      setNewServiceModalOpen(true);
      return;
    }
    const updated = [...newLineItems];
    const row = { ...updated[index], [field]: value };
    if (field === 'quantity_required' && row.item_type === 'service') {
      const r = parseFloat(value) || 0;
      row.quantity_required = Math.round(r * 100) / 100;
    }
    updated[index] = row;
    
    if (field === 'item_ref' && value) {
      const applied = applyCatalogRecordToLineRow(updated[index], catalogRecord);
      updated[index] = { ...updated[index], ...applied };
      if (applied.item_type === 'part') {
        const qn = Math.max(0, Math.trunc(Math.round(parseFloat(String(updated[index].quantity_required)) || 0)));
        updated[index].quantity_required = qn;
        updated[index].qty_open = qn - (updated[index].quantity_used || 0);
      }
    }
    
    if (field === 'quantity_required' || field === 'unit_price') {
      const qty =
        field === 'quantity_required'
          ? parseFloat(updated[index].quantity_required) || 0
          : parseFloat(updated[index].quantity_required) || 0;
      const price =
        field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(updated[index].unit_price) || 0;
      updated[index].line_total = qty * price;
      if (field === 'quantity_required') {
        updated[index].qty_open = qty - (updated[index].quantity_used || 0);
      }
    }
    
    setNewLineItems(updated);
  };

  const handleAddLineItemDraft = (itemType = 'part') => {
    const normalizedType = itemType === 'service' ? 'service' : 'part';
    setNewLineItems((prev) => ([
      ...prev,
      {
        item_type: normalizedType,
        part_id: normalizedType === 'part' ? '' : null,
        service_id: normalizedType === 'service' ? '' : null,
        unit: normalizedType === 'service' ? 'HR' : 'EA',
        quantity_required: 1,
        quantity_used: 0,
        qty_open: 1,
        unit_price: 0,
        description: '',
        notes: '',
        line_tax_mode: 'none',
        line_tax_percent: '',
        line_tax_fixed_amount: ''
      }
    ]));
  };

  const handleRemoveLineItem = (index) => {
    setNewLineItems(newLineItems.filter((_, i) => i !== index));
  };

  const handleNewPartCreated = (part, lineItemFields) => {
    if (!part) return;
    const qty = lineItemFields?.quantity != null && Number(lineItemFields.quantity) > 0 ? Number(lineItemFields.quantity) : 1;
    if (newPartModalRowIndex !== null) {
      const updated = [...newLineItems];
      updated[newPartModalRowIndex] = {
        ...updated[newPartModalRowIndex],
        item_type: 'part',
        part_id: part.id,
        service_id: '',
        part_number: part.part_number ?? '',
        service_code: '',
        description: lineItemFields?.description ?? part.part_description ?? part.description ?? '',
        quantity_required: qty,
        quantity_used: updated[newPartModalRowIndex].quantity_used ?? 0,
        qty_open: qty - (updated[newPartModalRowIndex].quantity_used ?? 0),
        unit_price: part.unit_price || 0,
        unit: part.unit || 'EA'
      };
      setNewLineItems(updated);
    } else if (newPartModalItemId) {
      const existing = editedLineItems[newPartModalItemId] || {};
      setEditedLineItems({
        ...editedLineItems,
        [newPartModalItemId]: {
          ...existing,
          part_id: part.id,
          part_number: part.part_number ?? '',
          description: lineItemFields?.description ?? part.part_description ?? part.description ?? '',
          quantity_required: qty,
          quantity_used: existing.quantity_used ?? 0,
          qty_open: qty - (existing.quantity_used ?? 0),
          unit_price: part.unit_price || 0,
          unit: part.unit || 'EA'
        }
      });
    }
    setNewPartModalOpen(false);
    setNewPartModalRowIndex(null);
    setNewPartModalItemId(null);
  };

  const handleNewServiceCreated = (service, lineItemFields) => {
    if (!service) return;
    const qty = lineItemFields?.quantity != null && Number(lineItemFields.quantity) > 0 ? Number(lineItemFields.quantity) : 1;
    const serviceCode = effectiveServiceCode(service) || '';
    if (newServiceModalRowIndex !== null) {
      const updated = [...newLineItems];
      const row = updated[newServiceModalRowIndex];
      updated[newServiceModalRowIndex] = {
        ...row,
        item_type: 'service',
        service_id: service.id,
        part_id: '',
        part_number: '',
        service_code: serviceCode,
        description: lineItemFields?.description ?? service.service_description ?? service.description ?? '',
        quantity_required: qty,
        quantity_used: row?.quantity_used ?? 0,
        qty_open: qty - (row?.quantity_used ?? 0),
        unit_price: service.unit_price || 0,
        unit: service.unit || 'HR'
      };
      const price = updated[newServiceModalRowIndex].unit_price || 0;
      updated[newServiceModalRowIndex].line_total = qty * price;
      setNewLineItems(updated);
    } else if (newServiceModalItemId) {
      const existing = editedLineItems[newServiceModalItemId] || {};
      const used = existing.quantity_used ?? 0;
      setEditedLineItems({
        ...editedLineItems,
        [newServiceModalItemId]: {
          ...existing,
          item_type: 'service',
          service_id: service.id,
          part_id: '',
          part_number: '',
          service_code: serviceCode,
          description: lineItemFields?.description ?? service.service_description ?? service.description ?? '',
          quantity_required: qty,
          quantity_used: used,
          qty_open: qty - used,
          unit_price: service.unit_price || 0,
          unit: service.unit || 'HR',
          line_total: qty * (service.unit_price || 0)
        }
      });
    }
    setNewServiceModalOpen(false);
    setNewServiceModalRowIndex(null);
    setNewServiceModalItemId(null);
  };

  const handleCancelEditLineItem = () => {
    setEditingItemId(null);
    setEditedLineItems({});
  };

  const handleApplyOrderLineTaxPatch = (itemId, patch) => {
    if (!patch) return;
    setEditedLineItems((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), ...patch }
    }));
  };

  const handleNewOrderLineTaxPatch = (index, patch) => {
    if (!patch) return;
    setNewLineItems((prev) => {
      const u = [...prev];
      if (!u[index]) return prev;
      u[index] = { ...u[index], ...patch };
      return u;
    });
  };

  const handleStartEditLineItem = (item) => {
    setEditingItemId(item.id);
    const qty = Number(item.quantity_required) || 0;
    const price = Number(item.unit_price) || 0;
    const itemType = (item.item_type || 'part') === 'service' ? 'service' : 'part';
    const qSrc =
      item.quantity_required != null && String(item.quantity_required).trim() !== ''
        ? item.quantity_required
        : qty;
    const quantity_required =
      itemType === 'service'
        ? formatQuantityDisplay(Number(qSrc) || 0, { itemType: 'service' }) || String(qSrc)
        : String(Math.max(0, Math.trunc(Math.round(Number(qSrc) || 0))));
    setEditedLineItems({
      [item.id]: {
        ...item,
        item_type: itemType,
        part_id: item.part_id != null && item.part_id !== '' ? String(item.part_id) : '',
        service_id: item.service_id != null && item.service_id !== '' ? String(item.service_id) : '',
        quantity_required,
        quantity_used: item.quantity_used ?? 0,
        unit_price: item.unit_price,
        unit: item.unit || (itemType === 'service' ? 'HR' : 'EA'),
        description: item.description || '',
        notes: item.notes || '',
        qty_open: item.qty_open,
        qty_to_stock: item.qty_to_stock,
        source_quote_line_item_id: item.source_quote_line_item_id,
        line_total: item.line_total != null ? item.line_total : qty * price,
        line_tax_mode: item.line_tax_mode || 'none',
        line_tax_percent:
          item.line_tax_percent != null && item.line_tax_percent !== ''
            ? formatTaxPercentDisplayValue(item.line_tax_percent)
            : '',
        line_tax_fixed_amount:
          item.line_tax_fixed_amount != null && item.line_tax_fixed_amount !== ''
            ? String(item.line_tax_fixed_amount)
            : ''
      }
    });
  };

  const handleRequestDeleteLineItem = (itemId) => {
    setLineItemToDelete(itemId);
    setDeleteLineItemDialogOpen(true);
  };

  const handleUpdateLineItemField = (itemId, field, value, catalogRecord = null) => {
    if (field === 'item_type') {
      const nextType = value === 'service' ? 'service' : 'part';
      const cur = editedLineItems[itemId] || {};
      setEditedLineItems({
        ...editedLineItems,
        [itemId]: {
          ...cur,
          item_type: nextType,
          part_id: nextType === 'part' ? (cur.part_id || '') : '',
          service_id: nextType === 'service' ? (cur.service_id || '') : '',
          unit: nextType === 'service' ? (cur.unit || 'HR') : (cur.unit || 'EA')
        }
      });
      return;
    }
    if (field === 'item_ref' && value === '__new_part__') {
      setNewPartModalRowIndex(null);
      setNewPartModalItemId(itemId);
      setNewPartModalOpen(true);
      return;
    }
    if (field === 'item_ref' && value === '__new_service__') {
      setNewServiceModalRowIndex(null);
      setNewServiceModalItemId(itemId);
      setNewServiceModalOpen(true);
      return;
    }
    if (field === 'quantity_required') {
      setEditedLineItems((prev) => {
        const cur = prev[itemId] || {};
        const used = cur.quantity_used || 0;
        const nextPrice = parseFloat(cur.unit_price) || 0;
        if (cur.item_type === 'service') {
          const qr = Math.round((parseFloat(String(value)) || 0) * 100) / 100;
          return {
            ...prev,
            [itemId]: {
              ...cur,
              quantity_required: value,
              line_total: Math.round(qr * nextPrice * 100) / 100,
              qty_open: Math.round((qr - used) * 100) / 100
            }
          };
        }
        const t = String(value).trim();
        if (t === '' || t === '-') {
          return {
            ...prev,
            [itemId]: {
              ...cur,
              quantity_required: '',
              line_total: 0,
              qty_open: -used
            }
          };
        }
        const n = Math.max(0, Math.trunc(Math.round(parseFloat(t))));
        return {
          ...prev,
          [itemId]: {
            ...cur,
            quantity_required: String(n),
            line_total: n * nextPrice,
            qty_open: n - used
          }
        };
      });
      return;
    }
    if (field === 'unit_price') {
      setEditedLineItems((prev) => {
        const cur = prev[itemId] || {};
        const nextPrice = parseFloat(value) || 0;
        const nextQty = parseFloat(String(cur.quantity_required)) || 0;
        const used = cur.quantity_used || 0;
        return {
          ...prev,
          [itemId]: {
            ...cur,
            unit_price: value,
            line_total: nextQty * nextPrice,
            qty_open: nextQty - used
          }
        };
      });
      return;
    }
    setEditedLineItems((prev) => {
      const cur = prev[itemId] || {};
      if (field === 'item_ref' && (value === '' || value == null)) {
        return {
          ...prev,
          [itemId]: {
            ...cur,
            part_id: '',
            service_id: '',
            item_type: 'part'
          }
        };
      }
      let next =
        field === 'item_ref' && value
          ? { ...cur }
          : { ...cur, [field]: value };
      if (field === 'item_ref' && value) {
        next = applyCatalogRecordToLineRow(next, catalogRecord);
        const used = next.quantity_used || 0;
        const nextPrice = parseFloat(next.unit_price) || 0;
        if (next.item_type === 'part') {
          const qn = Math.max(0, Math.trunc(Math.round(parseFloat(String(next.quantity_required)) || 0)));
          next.quantity_required = String(qn);
          next.line_total = qn * nextPrice;
          next.qty_open = qn - used;
        } else {
          const qs = parseFloat(String(next.quantity_required)) || 0;
          const qr = Math.round(qs * 100) / 100;
          next.quantity_required = String(formatQuantityDisplay(qr, { itemType: 'service' }));
          next.line_total = qr * nextPrice;
          next.qty_open = qr - used;
        }
      }
      return { ...prev, [itemId]: next };
    });
  };

  const handleSaveLineItem = async (itemId) => {
    try {
      setSaving(true);
      setError('');
      const itemData = editedLineItems[itemId];
      const existingLine = order?.lineItems?.find((li) => String(li.id) === String(itemId));
      const reqRaw = parseFloat(String(itemData.quantity_required)) || 0;
      const quantity_required =
        itemData.item_type === 'service'
          ? Math.round(reqRaw * 100) / 100
          : Math.max(0, Math.trunc(Math.round(reqRaw)));
      await orderService.updateLineItem(id, itemId, {
        item_type: itemData.item_type === 'service' ? 'service' : 'part',
        part_id: itemData.item_type === 'service' ? null : (itemData.part_id ? parseInt(itemData.part_id, 10) : null),
        service_id: itemData.item_type === 'service' ? (itemData.service_id ? parseInt(itemData.service_id, 10) : null) : null,
        quantity_required,
        quantity_used: parseFloat(itemData.quantity_used) || 0,
        unit_price: parseFloat(itemData.unit_price) || 0,
        unit: itemData.unit || (itemData.item_type === 'service' ? 'HR' : 'EA'),
        description: itemData.description || null,
        notes: itemData.notes != null && String(itemData.notes).trim() !== '' ? String(itemData.notes).trim() : null,
        source_quote_line_item_id: itemData.source_quote_line_item_id ?? existingLine?.source_quote_line_item_id ?? null,
        work_code: existingLine?.work_code ?? null,
        product_code: existingLine?.product_code ?? null,
        qty_to_stock: parseFloat(itemData.qty_to_stock) || 0,
        qty_open: parseFloat(itemData.qty_open) || 0,
        ...buildWorkOrderLineItemTaxPayload(itemData)
      });
      await refreshAfterOrderMutation();
      setEditingItemId(null);
      setEditedLineItems({});
    } catch (err) {
      console.error('Error updating line item:', err);
      setError(err.response?.data?.message || 'Failed to update line item');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const orderPathKey = location.pathname;
  const orderTabDirtyRef = useRef(false);
  useEffect(() => {
    orderTabDirtyRef.current =
      !!editedOrder ||
      editingItemId != null ||
      newLineItems.length > 0 ||
      Object.keys(editedLineItems).length > 0;
  });

  const flushOrderTabsRef = useRef(() => Promise.resolve());
  useEffect(() => {
    flushOrderTabsRef.current = async () => {
      if (editingItemId != null) await handleSaveLineItem(editingItemId);
      if (editedOrder) await handleSaveOrderEdit();
    };
  });

  const discardOrderTabsRef = useRef(() => Promise.resolve());
  useEffect(() => {
    discardOrderTabsRef.current = async () => {
      await handleCancelOrderEdit();
    };
  });

  useEffect(() => {
    const label = order?.identifier || order?.work_order_number;
    if (label) {
      registerTabTitle(orderPathKey, `Order ${label}`);
    }
  }, [order?.identifier, order?.work_order_number, orderPathKey, registerTabTitle]);

  useEffect(() => {
    return registerDirtyState(orderPathKey, {
      isDirty: () => orderTabDirtyRef.current,
      save: () => flushOrderTabsRef.current(),
      discard: () => discardOrderTabsRef.current()
    });
  }, [orderPathKey, registerDirtyState]);

  const confirmDeleteLineItem = async () => {
    if (!lineItemToDelete) return;
    
    try {
      await orderService.deleteLineItem(id, lineItemToDelete);
      await refreshAfterOrderMutation();
      setDeleteLineItemDialogOpen(false);
      setLineItemToDelete(null);
    } catch (err) {
      console.error('Error deleting line item:', err);
      setError(err.response?.data?.message || 'Failed to delete line item');
      setDeleteLineItemDialogOpen(false);
    }
  };

  const resetPrintLayout = () => {
    setPrintLayoutHideComments(true);
    setPrintLayoutHideDescription(true);
    setPrintLayoutSeparateLineSections(false);
  };

  const handlePrint = () => {
    document.body.classList.remove('wm-print-shop-docs-only');
    flushSync(() => {
      resetPrintLayout();
    });
    printWithFilename(orderPrintFilename);
  };

  const confirmDuplicateOrder = async () => {
    if (!id) return;
    setCopyOrderBusy(true);
    setError('');
    try {
      await releaseHeldEditLock();
      const res = await orderService.duplicate(id);
      const created = res?.data ?? res;
      const newId = created?.id;
      if (!newId) {
        throw new Error('Server did not return the new order id.');
      }
      setCopyOrderDialogOpen(false);
      navigate(`/work-orders/${newId}`);
    } catch (err) {
      console.error('Duplicate order failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to duplicate order');
    } finally {
      setCopyOrderBusy(false);
    }
  };

  const handlePrintShopDocs = async () => {
    if (!id || shopDocPrintBusy) return;
    setShopDocPrintBusy(true);
    setAttachmentsError('');
    try {
      const res = await attachmentService.getForWorkOrder(id);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      const shopAtts = list.filter((a) => Array.isArray(a?.categories) && a.categories.includes('shop'));
      const assets = [];
      for (const a of shopAtts) {
        const r = await attachmentService.download(a.id);
        const blob = r.data;
        const ct = String(a.content_type || blob.type || '').toLowerCase();
        const name = a.file_name || `Attachment-${a.id}`;
        const looksPdf = ct.includes('pdf') || /\.pdf\s*$/i.test(String(name));
        let slides = [];
        if (ct.startsWith('image/')) {
          const url = URL.createObjectURL(blob);
          slides = [{ src: url, isBlob: true }];
        } else if (looksPdf) {
          try {
            const dataUrls = await rasterizePdfBlobToJpegDataUrls(blob);
            slides =
              dataUrls.length > 0
                ? dataUrls.map((src) => ({ src, isBlob: false }))
                : [{ unsupported: true }];
          } catch (pdfErr) {
            console.error('Shop docs PDF rasterize:', pdfErr);
            slides = [{ unsupported: true }];
          }
        } else {
          slides = [{ unsupported: true }];
        }
        assets.push({ id: a.id, file_name: name, slides });
      }
      flushSync(() => {
        resetPrintLayout();
        setShopDocPrintAssets(assets);
      });
      document.body.classList.add('wm-print-shop-docs-only');
      const cleanup = () => {
        document.body.classList.remove('wm-print-shop-docs-only');
        assets.forEach((asset) => {
          (asset.slides || []).forEach((s) => {
            if (s.isBlob && s.src) URL.revokeObjectURL(s.src);
          });
        });
        setShopDocPrintAssets(null);
      };
      window.addEventListener('afterprint', cleanup, { once: true });
      setTimeout(cleanup, 2500);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          printWithFilename(`${orderPrintFilename}-shop-docs`);
        });
      });
    } catch (err) {
      console.error('Shop docs print:', err);
      setAttachmentsError(err.response?.data?.message || 'Failed to prepare shop documents for print');
    } finally {
      setShopDocPrintBusy(false);
    }
  };

  const handlePrintFromSettingsDialog = () => {
    document.body.classList.remove('wm-print-shop-docs-only');
    const { hideComments, hideDescription, separateLineSections } = printSettingsDraft;
    flushSync(() => {
      setPrintLayoutHideComments(hideComments);
      setPrintLayoutHideDescription(hideDescription);
      setPrintLayoutSeparateLineSections(!!separateLineSections);
      setOrderPrintSettingsOpen(false);
    });
    const restoreLayout = () => {
      resetPrintLayout();
    };
    window.addEventListener('afterprint', restoreLayout, { once: true });
    setTimeout(restoreLayout, 2500);
    // Defer print so the modal unmounts before the browser captures the page (work order only, not the popup).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        printWithFilename(orderPrintFilename);
      });
    });
  };

  const getStatusChipSx = (status) => {
    if (status === 'open') {
      return { textTransform: 'uppercase', backgroundColor: '#FFE0B2', color: '#E65100', fontWeight: 700 };
    }
    if (status === 'waiting_external') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF3E0', color: '#EF6C00', fontWeight: 700 };
    }
    if (status === 'Partial') {
      return { textTransform: 'uppercase', backgroundColor: '#FFF9C4', color: '#F57F17', fontWeight: 700 };
    }
    if (status === 'Ready to Ship') {
      return { textTransform: 'uppercase', backgroundColor: '#C8E6C9', color: '#1B5E20', fontWeight: 700 };
    }
    if (status === 'shipped') {
      return { textTransform: 'uppercase', backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: 700 };
    }
    return { textTransform: 'capitalize' };
  };

  // Prefer immutable identifier; fall back to raw numeric quote number.
  const formatOrderQuoteNumber = (o) => {
    if (!o || !o.quote_id) return '—';
    if (o.quote_identifier) return o.quote_identifier;
    if (o.quote_number == null && o.quote_number !== 0) return '—';
    return String(o.quote_number);
  };
  const getOrderSourceQuoteReference = (o) => {
    const notes = String(o?.notes || '');
    if (!notes) return '';
    const sourceMatch = notes.match(/(?:Source quote|Reference quote):\s*([^\n\r]+)/i);
    if (sourceMatch?.[1]) return sourceMatch[1].trim();
    const partialCreated = notes.match(/Order and Quote created from\s+([^\n\r]+)/i);
    if (partialCreated?.[1]) return partialCreated[1].trim();
    return '';
  };
  const referenceQuoteIdentifier = getOrderSourceQuoteReference(order);
  const navigateToReferenceQuote = async () => {
    if (!referenceQuoteIdentifier) return;
    const bracketId = referenceQuoteIdentifier.match(/^(.+?)\s+\[(\d+)\]\s*$/);
    if (bracketId && bracketId[2]) {
      navigate(`/quotes/${bracketId[2]}`);
      return;
    }
    const searchTerm = bracketId ? bracketId[1].trim() : referenceQuoteIdentifier;
    try {
      const res = await axios.get('/api/quotes', {
        params: { search: searchTerm, limit: 25, offset: 0 }
      });
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      const exact = rows.find((q) => String(q?.identifier || '').trim() === searchTerm)
        || rows.find((q) => String(q?.quote_number || '').trim() === searchTerm)
        || rows[0];
      if (exact?.id) {
        navigate(`/quotes/${exact.id}`);
        return;
      }
      setError(`Could not find quote "${referenceQuoteIdentifier}"`);
    } catch (err) {
      setError(err?.response?.data?.message || `Could not open quote "${referenceQuoteIdentifier}"`);
    }
  };

  /** Outlined `small` fields: target `.MuiOutlinedInput-input` (not generic InputBase) so padding matches the notch; avoid custom label fontSize (breaks transforms). */
  const orderDenseFieldSx = {
    mt: '8px',
    mb: 0.65,
    '& .MuiOutlinedInput-input': {
      fontSize: '0.8125rem',
      lineHeight: 1.4,
      padding: '5px 10px'
    }
  };

  /** Always keep labels in the shrunk slot so they never collide with the border (esp. disabled + value). */
  const orderDenseInputLabelProps = { shrink: true };

  /** Fulfillment & shipping tables: shared type scale (slightly bumped for readability). */
  const fulfillTableCellFont = '0.75rem';
  const fulfillSectionBarFont = '0.7rem';
  const fulfillColHeaderFont = '0.6875rem';

  /** T&M line items: same visual language as Quote Detail / Quote New (#50a8ee bands, #aed9f7 column headers). */
  const orderTmLineItemColumnSizing = {
    '& th:nth-of-type(1), & td:nth-of-type(1)': { width: '4% !important', minWidth: 56, textAlign: 'center' },
    '& th:nth-of-type(2), & td:nth-of-type(2)': { width: '7% !important', minWidth: 80, textAlign: 'center' },
    '& th:nth-of-type(3), & td:nth-of-type(3)': { width: '16% !important', minWidth: 200 },
    '& th:nth-of-type(4), & td:nth-of-type(4)': { width: '6% !important', minWidth: 80, textAlign: 'center' },
    '& th:nth-of-type(5), & td:nth-of-type(5)': { width: '6% !important', minWidth: 72, textAlign: 'center' },
    '& th:nth-of-type(6), & td:nth-of-type(6)': { width: '8% !important', minWidth: 92, textAlign: 'right' },
    '& th:nth-of-type(7), & td:nth-of-type(7)': { width: '5.5% !important', minWidth: 88, textAlign: 'right' },
    '& th:nth-of-type(8), & td:nth-of-type(8)': { width: '11% !important', minWidth: 128, textAlign: 'right' },
    '& th:nth-of-type(9), & td:nth-of-type(9)': { width: '8% !important', minWidth: 96, textAlign: 'right' },
    '& th:nth-of-type(10), & td:nth-of-type(10)': { width: '8% !important', minWidth: 80, textAlign: 'center' },
    '& th:nth-of-type(11), & td:nth-of-type(11)': { width: '12% !important', minWidth: 160 },
    '& th:nth-of-type(12), & td:nth-of-type(12)': { width: '8% !important', minWidth: 112, textAlign: 'center' }
  };
  const orderTmQuoteStyleLineItemTableSx = {
    borderCollapse: 'collapse',
    border: '1px solid #e0e0e0',
    ...orderTmLineItemColumnSizing,
    '& .MuiTableCell-head': { verticalAlign: 'middle' },
    '& .MuiTextField-root .MuiInputBase-root': { fontSize: '0.8125rem' },
    '& .MuiTextField-root .MuiOutlinedInput-input': {
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      padding: '4px 8px'
    },
    '& td:nth-of-type(4) .MuiOutlinedInput-input, & td:nth-of-type(5) .MuiOutlinedInput-input': {
      textAlign: 'center'
    },
    '& td:nth-of-type(6) .MuiOutlinedInput-input, & td:nth-of-type(7) .MuiOutlinedInput-input, & td:nth-of-type(8) .MuiOutlinedInput-input, & td:nth-of-type(9) .MuiOutlinedInput-input': {
      textAlign: 'right'
    },
    '& .MuiInputBase-input': { py: 0.5 },
    '& .MuiFormControl-root': {
      width: '100%',
      minWidth: 0,
      '& .MuiInputLabel-root': { fontSize: '0.75rem', lineHeight: 1.35 }
    },
    '& td:nth-of-type(7) .MuiFormControl-root': { minWidth: 72 },
    '& td:nth-of-type(8) .MuiFormControl-root': { minWidth: 112 },
    '& .MuiTextField-root': { width: '100%' },
    '& .MuiFormControl-root .MuiOutlinedInput-root .MuiSelect-select': {
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      fontWeight: 400,
      padding: '4px 8px',
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    /* New-order draft part picker: ~45ch wide then wrap (overrides col 3 minWidth: 200). */
    '& td.wm-order-new-draft-part-cell': {
      maxWidth: '45ch !important',
      width: '45ch !important',
      minWidth: '0 !important',
      verticalAlign: 'middle',
      boxSizing: 'border-box'
    },
    '& td.wm-order-new-draft-part-cell .MuiFormControl-root': {
      maxWidth: '100%',
      marginTop: 0,
      marginBottom: 0
    },
    '& td.wm-order-new-draft-part-cell .MuiOutlinedInput-root .MuiSelect-select': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
      textOverflow: 'clip',
      display: 'block',
      alignItems: 'unset',
      padding: '4px 8px',
      lineHeight: 1.35,
      minHeight: 0
    },
    '& tbody tr.wm-line-totals-footer > td:nth-of-type(1), & tbody tr.wm-line-totals-footer > td:nth-of-type(2)': {
      textAlign: 'right'
    },
    '& tbody tr.wm-line-totals-footer--subtotal > td:nth-of-type(1)': { textAlign: 'left' },
    '& .MuiChip-root': { height: 22, fontSize: '0.7rem' },
    '& .MuiIconButton-root': { p: 0.35 }
  };
  const orderTmLineItemIdxBoxSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    px: 0.5,
    py: 0.25,
    minHeight: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8125rem'
  };
  const orderTmLineHdrCellSx = {
    whiteSpace: 'nowrap',
    border: '1px solid #ccc',
    px: 0.75,
    py: 0.5,
    fontSize: '0.75rem',
    // Opaque blend of former #1e98e95c over white — semi-transparent headers let body labels bleed through.
    bgcolor: '#aed9f7',
    fontWeight: 600,
    position: 'relative',
    zIndex: 2,
    overflow: 'hidden'
  };
  const orderTmLineBodyCellSx = {
    border: '1px solid #e0e0e0',
    px: 0.75,
    py: 0.5,
    fontSize: '0.8125rem',
    verticalAlign: 'middle'
  };

  /** Fulfillment & shipping sub-table: wide Part column; tight # / code / qty columns (colgroup + first row colspan). */
  const fulfillLW = {
    num: 22,
    code: 45,
    part: 300,
    qty: 26,
    onPs: 28,
    open: 26,
    shipped: 28,
    lineTotal: 54
  };
  const fulfillTableMinWidth =
    fulfillLW.num +
    fulfillLW.code +
    fulfillLW.part +
    fulfillLW.qty +
    fulfillLW.onPs +
    fulfillLW.open +
    fulfillLW.shipped +
    fulfillLW.lineTotal;

  /** Fulfillment & shipping (slate / steel) — line-items grid uses the same visual language. */
  const orderFulfillmentTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: fulfillTableMinWidth,
    maxWidth: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #90a4ae',
    backgroundColor: '#fff',
    fontSize: fulfillTableCellFont,
    '& .MuiTableCell-root': {
      fontSize: fulfillTableCellFont,
      py: '2px',
      px: '4px',
      lineHeight: 1.2,
      verticalAlign: 'middle',
      borderBottom: '1px solid #cfd8dc',
      borderRight: '1px solid #eceff1'
    },
  };

  const getOpenToShipQty = (item) => {
    const required = Number(item?.quantity_required) || 0;
    const shipped = Number(item?.quantity_shipped) || 0;
    return Math.max(0, Math.trunc(required - shipped));
  };

  const getShippedQty = (item) => {
    return Math.max(0, Math.trunc(Number(item?.quantity_shipped_with_ship_date) || 0));
  };

  /** Line-level fulfillment (not quote pending/won/lost); mirrors packing-slip quantities. */
  const getLineFulfillmentStatus = (item) => {
    const req = Number(item?.quantity_required) || 0;
    if (req <= 0) return { label: '—', color: 'default' };
    const onSlips = Math.max(0, Math.trunc(Number(item?.quantity_shipped) || 0));
    const open = getOpenToShipQty(item);
    const shippedDated = getShippedQty(item);
    if (shippedDated >= req) return { label: 'Shipped', color: 'primary' };
    if (open === 0 && onSlips > 0) return { label: 'Ready to ship', color: 'warning' };
    if (open > 0 && (onSlips > 0 || shippedDated > 0)) return { label: 'Partial', color: 'warning' };
    return { label: 'Open', color: 'default' };
  };

  const openLineTextPopout = (title, value) => {
    setLineTextPopoutTitle(title || 'Line item details');
    setLineTextPopoutValue(String(value || '').trim());
    setLineTextPopoutOpen(true);
  };

  const closeLineTextPopout = () => {
    setLineTextPopoutOpen(false);
    setLineTextPopoutTitle('');
    setLineTextPopoutValue('');
  };

  const partLines = useMemo(
    () => order?.lineItems?.filter((li) => (li.item_type || 'part') !== 'service') ?? [],
    [order?.lineItems]
  );
  const serviceLines = useMemo(
    () => order?.lineItems?.filter((li) => (li.item_type || 'part') === 'service') ?? [],
    [order?.lineItems]
  );

  const partsMaterialsLineTotal = useMemo(() => {
    return partLines.reduce((sum, item) => {
      const isEditing = String(editingItemId) === String(item.id);
      const editedItem = editedLineItems[item.id] || item;
      let amt = 0;
      if (isEditing) {
        if (editedItem.line_total != null) amt = Number(editedItem.line_total) || 0;
        else amt = (Number(editedItem.quantity_required) || 0) * (Number(editedItem.unit_price) || 0);
      } else if (item.line_total != null) amt = Number(item.line_total) || 0;
      else amt = (Number(item.quantity_required) || 0) * (Number(item.unit_price) || 0);
      return sum + amt;
    }, 0);
  }, [partLines, editedLineItems, editingItemId]);

  const laborServicesLineTotal = useMemo(() => {
    return serviceLines.reduce((sum, item) => {
      const isEditing = String(editingItemId) === String(item.id);
      const editedItem = editedLineItems[item.id] || item;
      let amt = 0;
      if (isEditing) {
        if (editedItem.line_total != null) amt = Number(editedItem.line_total) || 0;
        else amt = (Number(editedItem.quantity_required) || 0) * (Number(editedItem.unit_price) || 0);
      } else if (item.line_total != null) amt = Number(item.line_total) || 0;
      else amt = (Number(item.quantity_required) || 0) * (Number(item.unit_price) || 0);
      return sum + amt;
    }, 0);
  }, [serviceLines, editedLineItems, editingItemId]);

  const newLineItemsDraftTotal = useMemo(
    () =>
      newLineItems.reduce(
        (sum, row) => sum + (Number(row.quantity_required) || 0) * (Number(row.unit_price) || 0),
        0
      ),
    [newLineItems]
  );

  const lineItemsGrandTotal = partsMaterialsLineTotal + laborServicesLineTotal + newLineItemsDraftTotal;

  const orderTaxBasisUi = normalizeTaxBasisUi(order?.tax_basis);
  const isPerLineOrderTax = !!order && !isScopeOfWorkOrder(order) && orderTaxBasisUi === 'per_line';
  const orderLineItemsTaxEditActive = !!order && !isScopeOfWorkOrder(order) && editing && !isHistoricalOrder;
  const orderTaxScopeToggleValue = !order || isScopeOfWorkOrder(order)
    ? 'no_tax'
    : isPerLineOrderTax
      ? 'line_items'
      : normalizeOrderTaxModeUi(order.tax_mode) === 'none'
        ? 'no_tax'
        : 'order';

  const orderLineItemsTaxSumLive = useMemo(() => {
    if (!isPerLineOrderTax) return 0;
    let sum = 0;
    const addRow = (row) => {
      const sub = computeOrderTmLineSubtotal(row);
      sum += computeLineItemTaxAmount(sub, row);
    };
    for (const item of partLines) {
      const row =
        String(editingItemId) === String(item.id) && editedLineItems[item.id]
          ? editedLineItems[item.id]
          : item;
      addRow(row);
    }
    for (const item of serviceLines) {
      const row =
        String(editingItemId) === String(item.id) && editedLineItems[item.id]
          ? editedLineItems[item.id]
          : item;
      addRow(row);
    }
    for (const row of newLineItems) {
      addRow(row);
    }
    return Math.round(sum * 100) / 100;
  }, [isPerLineOrderTax, partLines, serviceLines, editedLineItems, editingItemId, newLineItems]);

  const orderDisplayDiscount = computeOrderDisplayDiscount(
    lineItemsGrandTotal,
    orderDiscountDraft.mode,
    orderDiscountDraft.percentStr,
    orderDiscountDraft.fixedStr
  );
  const orderNetMerchandise = Math.max(0, Math.round((lineItemsGrandTotal - orderDisplayDiscount) * 100) / 100);
  const orderDisplayTax = isPerLineOrderTax
    ? (lineItemsGrandTotal > 0.0001
      ? Math.round(orderLineItemsTaxSumLive * (orderNetMerchandise / lineItemsGrandTotal) * 100) / 100
      : 0)
    : computeOrderDisplayTax(
        orderNetMerchandise,
        orderTaxDraft.mode,
        orderTaxDraft.percentStr,
        orderTaxDraft.fixedStr
      );
  const orderDisplayGrandTotal = Math.round((orderNetMerchandise + orderDisplayTax) * 100) / 100;

  const orderLineSubtotalColumnAmount = (row) => {
    const base = computeOrderTmLineSubtotal(row);
    if (!isPerLineOrderTax) {
      if (row?.line_total != null && row.line_total !== '') {
        const n = Number(row.line_total);
        if (Number.isFinite(n)) return Math.round(n * 100) / 100;
      }
      return base;
    }
    return computeLineTotalWithPerLineTax(base, row);
  };

  const renderOrderLineTaxCells = ({ row, isEditing, disabled, onLineTaxPatch }) => {
    const sub = computeOrderTmLineSubtotal(row);
    if (!isPerLineOrderTax) {
      return [
        <TableCell key="olt-ctrl" sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
          —
        </TableCell>,
        <TableCell key="olt-amt" sx={{ ...orderTmLineBodyCellSx, textAlign: 'right' }}>
          —
        </TableCell>
      ];
    }
    const mode = normalizeLineTaxModeUi(row.line_tax_mode);
    const taxAmt = computeLineItemTaxAmount(sub, row);
    const taxAmtCents = Math.round((Number(taxAmt) || 0) * 100) / 100;
    if (isEditing) {
      const amountFieldValue = mode === 'none' && taxAmtCents === 0 ? '' : taxAmtCents;
      return [
        <TableCell
          key="olt-pct"
          sx={{ ...orderTmLineBodyCellSx, minWidth: 88, px: 0.35, textAlign: 'right' }}
        >
          <TextField
            size="small"
            type="number"
            disabled={disabled}
            aria-label="Line tax percent"
            value={lineTaxPercentFieldValue(row, sub)}
            onChange={(e) => {
              const patch = lineTaxPatchFromPercentInput(sub, e.target.value);
              if (patch) onLineTaxPatch(patch);
            }}
            onKeyDown={(e) => handleLineTaxPercentArrowKeyDown(e, row, sub, onLineTaxPatch)}
            inputProps={{
              min: 0,
              step: 'any',
              style: { textAlign: 'right' },
              ...lineTaxPercentInputPropsExtra()
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" disableTypography sx={{ mr: 0.25 }}>
                  <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1 }}>
                    %
                  </Box>
                </InputAdornment>
              )
            }}
            sx={lineItemTaxPercentEditFieldSx}
          />
        </TableCell>,
        <TableCell key="olt-amt" sx={{ ...orderTmLineBodyCellSx, textAlign: 'right', minWidth: 128 }}>
          <CurrencyTextField
            size="small"
            disabled={disabled}
            aria-label="Line tax amount"
            value={amountFieldValue}
            onChange={(e) => {
              const patch = lineTaxPatchFromAmountInput(sub, e.target.value);
              if (patch) onLineTaxPatch(patch);
            }}
            minimumFractionDigits={2}
            maximumFractionDigits={2}
            dollarAdornment
            inputProps={{ style: { textAlign: 'right' } }}
            sx={lineItemTaxDollarsEditFieldSx}
          />
        </TableCell>
      ];
    }
    return [
      <TableCell
        key="olt-pct"
        sx={{ ...orderTmLineBodyCellSx, textAlign: 'right', minWidth: 88, px: 0.35 }}
      >
        {lineTaxPercentLabel(row, sub)}
      </TableCell>,
      <TableCell key="olt-amt" sx={{ ...orderTmLineBodyCellSx, textAlign: 'right', minWidth: 128 }}>
        {formatLineItemTaxDollars(taxAmtCents)}
      </TableCell>
    ];
  };

  const newPartDraftRows = useMemo(
    () => newLineItems.map((item, index) => ({ item, index })).filter(({ item }) => item.item_type !== 'service'),
    [newLineItems]
  );
  const newServiceDraftRows = useMemo(
    () => newLineItems.map((item, index) => ({ item, index })).filter(({ item }) => item.item_type === 'service'),
    [newLineItems]
  );

  const orderInvoiceFinancialTotals = useMemo(() => {
    const ir = order?.invoice_rollup;
    if (ir && ir.order_total_amount != null) {
      const orderTotal = Number(ir.order_total_amount) || 0;
      const totalInvoiced = Number(ir.invoiced_total) || 0;
      const uninvoicedVsOrder = Number(ir.uninvoiced_amount);
      const outstanding = Number(ir.open_invoice_balance_due) || 0;
      const invoicingMatchesOrder = Math.abs(uninvoicedVsOrder) < 0.02;
      return {
        totalInvoiced,
        outstanding,
        orderTotal,
        uninvoicedVsOrder,
        invoicingMatchesOrder,
        amountPaidTotal: Number(ir.amount_paid_total) || 0,
        hasPastDueInvoice: !!ir.has_past_due_invoice
      };
    }
    const list = orderInvoices || [];
    const totalInvoiced = list.reduce((s, inv) => s + (Number(inv.total_amount) || 0), 0);
    const outstanding = list.reduce((s, inv) => {
      const total = Number(inv.total_amount) || 0;
      const paid = Number(inv.amount_paid) || 0;
      return s + Math.max(0, total - paid);
    }, 0);
    const amountPaidTotal = list.reduce((s, inv) => s + (Number(inv.amount_paid) || 0), 0);
    const orderTotal = Number(order?.total_amount) || 0;
    const uninvoicedVsOrder = orderTotal - totalInvoiced;
    const invoicingMatchesOrder = Math.abs(uninvoicedVsOrder) < 0.02;
    return {
      totalInvoiced,
      outstanding,
      orderTotal,
      uninvoicedVsOrder,
      invoicingMatchesOrder,
      amountPaidTotal,
      hasPastDueInvoice: false
    };
  }, [orderInvoices, order?.total_amount, order?.invoice_rollup]);

  /** What the completion banner links to (blockers + invoicing/payment gaps). */
  const completionAwaitingItems = useMemo(() => {
    if (!completionReadiness || completionReadiness.ready) return [];
    const codes = new Set((completionReadiness.blockers || []).map((b) => String(b.code || '')));
    const items = [];
    if (codes.has('customer_fulfillment')) {
      items.push({ key: 'shipments', label: 'Shipments', action: 'tab', tab: orderDetailTabs.shipments });
    }
    if (['outside_process', 'open_po', 'procurement_receive'].some((c) => codes.has(c))) {
      items.push({ key: 'thirdParty', label: 'Third-party / subcontract', action: 'scroll' });
    }
    const { uninvoicedVsOrder, outstanding } = orderInvoiceFinancialTotals;
    const needsInvoicing = uninvoicedVsOrder > 0.02;
    const needsPayment = outstanding > 0.02;
    if (needsInvoicing || needsPayment) {
      let label = 'Invoicing and payments';
      if (needsInvoicing && !needsPayment) label = 'Invoicing';
      if (!needsInvoicing && needsPayment) label = 'Payments';
      items.push({ key: 'invoicePay', label, action: 'tab', tab: orderDetailTabs.invoices });
    }
    return items;
  }, [completionReadiness, orderInvoiceFinancialTotals, orderDetailTabs]);

  const completionBannerLinkButtonSx = {
    p: 0,
    minWidth: 0,
    textTransform: 'none',
    verticalAlign: 'baseline',
    fontSize: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline'
  };

  /** Customer slips: not fully shipped with a ship date (req − qty on dated customer shipments). */
  const notYetShippedLineRows = useMemo(() => {
    const lines = order?.lineItems || [];
    return lines
      .map((li) => {
        const req = Math.max(0, Number(li.quantity_required) || 0);
        const onSlip = Math.max(0, Number(li.quantity_shipped) || 0);
        const dated = Math.max(0, Number(li.quantity_shipped_with_ship_date) || 0);
        const remaining = Math.max(0, req - dated);
        let reason = '';
        if (onSlip <= 0.000001) {
          reason = 'Not on customer packing slip';
        } else {
          const bits = [];
          if (dated < onSlip - 0.000001) {
            bits.push(`${formatQuantityDisplay(onSlip - dated)} on slip(s) without ship date`);
          }
          if (req > onSlip + 0.000001) {
            bits.push(`${formatQuantityDisplay(req - onSlip)} not yet on a packing slip`);
          }
          reason = bits.join(' · ') || '—';
        }
        const label =
          (li.item_type || 'part') === 'service'
            ? effectiveServiceCode(li) || li.service_name || li.description || `Line ${li.id}`
            : li.part_number || li.part_name || li.description || `Line ${li.id}`;
        const sub = (li.item_type || 'part') === 'service' ? li.service_name || li.description || '' : li.part_name || li.description || '';
        return { li, req, onSlip, dated, remaining, reason, label, sub };
      })
      .filter((row) => row.remaining > 0.000001)
      .sort((a, b) => (a.li.id || 0) - (b.li.id || 0));
  }, [order?.lineItems]);

  const invoicesByPackingSlipId = useMemo(() => {
    const m = new Map();
    for (const inv of orderInvoices || []) {
      const sid = inv?.packing_slip_id;
      if (sid == null || sid === '') continue;
      const key = String(sid);
      if (!m.has(key)) m.set(key, inv);
    }
    return m;
  }, [orderInvoices]);

  const sortedPackingSlipsForInvoiceTab = useMemo(() => {
    const list = [...(packingSlips || [])];
    const num = (s) => Number(s?.packing_slip_number);
    list.sort((a, b) => {
      const na = num(a);
      const nb = num(b);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return String(a?.identifier ?? '').localeCompare(String(b?.identifier ?? ''));
    });
    return list;
  }, [packingSlips]);

  const summaryInvoicesByCreatedAsc = useMemo(() => {
    const list = [...(orderInvoices || [])];
    list.sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : Infinity;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : Infinity;
      return (Number.isNaN(ta) ? Infinity : ta) - (Number.isNaN(tb) ? Infinity : tb);
    });
    return list;
  }, [orderInvoices]);

  const summaryPackingSlipsByCreatedAsc = useMemo(() => {
    const list = [...(packingSlips || [])];
    list.sort((a, b) => {
      const ta = a?.created_at ? new Date(a.created_at).getTime() : Infinity;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : Infinity;
      return (Number.isNaN(ta) ? Infinity : ta) - (Number.isNaN(tb) ? Infinity : tb);
    });
    return list;
  }, [packingSlips]);

  /** Distinct vendor POs tied to this order (header or line link). Merge header list + line-level rows. */
  const linkedPurchaseOrdersForOrder = useMemo(() => {
    const ps = procurementSummary;
    if (!ps) return [];
    const byId = new Map();
    for (const row of ps.linked_purchase_orders || []) {
      const pid = Number(row.purchase_order_id);
      if (!Number.isFinite(pid)) continue;
      byId.set(pid, {
        ...row,
        purchase_order_id: pid
      });
    }
    for (const poi of ps.linked_po_items || []) {
      const pid = Number(poi.purchase_order_id);
      if (!Number.isFinite(pid)) continue;
      if (!byId.has(pid)) {
        byId.set(pid, {
          purchase_order_id: pid,
          po_identifier: poi.po_identifier,
          po_number: poi.po_number,
          vendor_name: poi.vendor_name,
          po_status: poi.po_status,
          order_date: null,
          expected_date: poi.expected_date ?? null
        });
      }
    }
    return [...byId.values()].sort((a, b) => b.purchase_order_id - a.purchase_order_id);
  }, [procurementSummary]);

  const getCustomerInvoiceStatusColor = (status) => {
    const colors = { Due: 'info', 'Past Due': 'error', Paid: 'success', Voided: 'default' };
    return colors[status] || 'default';
  };

  const packingSlipPurposeLabel = (purpose) => {
    const p = String(purpose || '').toLowerCase();
    if (p === 'vendor_outbound') return 'Vendor outbound';
    return 'Customer';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Alert severity="error">{error || 'Order not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/work-orders')} size="small" sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  const orderEditSaveBar =
    editing && !isHistoricalOrder ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'primary.light',
          bgcolor: 'rgba(25, 118, 210, 0.07)'
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="medium"
          startIcon={<Save />}
          onClick={handleSaveOrderEdit}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save order'}
        </Button>
        <Button variant="outlined" size="medium" onClick={handleCancelOrderEdit} disabled={saving}>
          Cancel
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 220px', fontSize: '0.8125rem', lineHeight: 1.5 }}>
          Saves description, dates, status, comments, and any new line-item drafts. Use Save on each row to update an existing line.
        </Typography>
      </Box>
    ) : null;

  const orderPrintLineExtension = (item) => {
    const amt =
      item.line_total != null && item.line_total !== ''
        ? Number(item.line_total) || 0
        : (Number(item.quantity_required) || 0) * (Number(item.unit_price) || 0);
    return Math.round(amt * 100) / 100;
  };
  const getOrderPrintLineLayout = (hideFinancials) => {
    const showTaxCol = !hideFinancials && isPerLineOrderTax;
    const lineColCount = hideFinancials ? 6 : isPerLineOrderTax ? 10 : 9;
    const subtotalLabelColSpan = hideFinancials ? 5 : isPerLineOrderTax ? 9 : 8;
    return { hideFinancials, showTaxCol, lineColCount, subtotalLabelColSpan };
  };

  const orderPrintTaxColTh = {
    border: '1px solid #000',
    padding: '4px 4px',
    fontSize: '11px',
    textAlign: 'right',
    backgroundColor: '#f0f0f0',
    width: '52px',
    maxWidth: '56px',
    whiteSpace: 'nowrap'
  };
  const orderPrintTaxColTd = {
    border: '1px solid #000',
    padding: '4px 4px',
    fontSize: '11px',
    textAlign: 'right'
  };

  const renderOrderPrintLineTableHead = (layout) => (
    <thead>
      <tr>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Code</th>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Description</th>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty Req</th>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty Used</th>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty Shipped</th>
        <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty Open</th>
        {!layout.hideFinancials ? (
          <>
            <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Unit</th>
            <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Unit Price</th>
            {layout.showTaxCol ? <th style={orderPrintTaxColTh}>Tax</th> : null}
            <th style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Line Total</th>
          </>
        ) : null}
      </tr>
    </thead>
  );

  const renderOrderPrintLineRows = (lines, layout) =>
    lines.map((item, index) => {
      if (layout.hideFinancials) {
        return (
          <tr key={item.id ?? `oi-${index}`}>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px' }}>
              {item.item_type === 'service' ? effectiveServiceCode(item) || '-' : item.part_number || '-'}
            </td>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
              {item.description || item.part_name || item.service_name || '-'}
              {item.notes && String(item.notes).trim() ? `\n\nNotes: ${item.notes}` : ''}
            </td>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_required ?? 0)}</td>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_used ?? 0)}</td>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_shipped ?? 0)}</td>
            <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.qty_open ?? 0)}</td>
          </tr>
        );
      }
      const sub = orderPrintLineExtension(item);
      const taxAmt = isPerLineOrderTax ? computeLineItemTaxAmount(sub, item) : 0;
      const taxCell =
        isPerLineOrderTax && Math.round(taxAmt * 100) / 100 > 0 ? formatLineItemTaxDollars(taxAmt) : '';
      const lineTot = isPerLineOrderTax ? computeLineTotalWithPerLineTax(sub, item) : sub;
      return (
        <tr key={item.id ?? `oi-${index}`}>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px' }}>
            {item.item_type === 'service' ? effectiveServiceCode(item) || '-' : item.part_number || '-'}
          </td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
            {item.description || item.part_name || item.service_name || '-'}
            {item.notes && String(item.notes).trim() ? `\n\nNotes: ${item.notes}` : ''}
          </td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_required ?? 0)}</td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_used ?? 0)}</td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_shipped ?? 0)}</td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.qty_open ?? 0)}</td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px' }}>{item.unit || (item.item_type === 'service' ? 'HR' : 'EA')}</td>
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(item.unit_price || 0)}</td>
          {isPerLineOrderTax ? <td style={orderPrintTaxColTd}>{taxCell}</td> : null}
          <td style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(lineTot)}</td>
        </tr>
      );
    });

  const orderPrintEmptyRowTd = { border: '1px solid #000', padding: '6px 6px', fontSize: '11px' };
  const renderOrderPrintEmptyBodyRow = (layout) => (
    <tr>
      {Array.from({ length: layout.lineColCount }, (_, i) => (
        <td key={i} style={orderPrintEmptyRowTd}>&nbsp;</td>
      ))}
    </tr>
  );

  const sumOrderPrintLines = (lines) =>
    lines.reduce((sum, item) => {
      const amt =
        item.line_total != null && item.line_total !== ''
          ? Number(item.line_total) || 0
          : (Number(item.quantity_required) || 0) * (Number(item.unit_price) || 0);
      return sum + amt;
    }, 0);
  const orderPrintPartsSubtotal = sumOrderPrintLines(partLines);
  const orderPrintLaborSubtotal = sumOrderPrintLines(serviceLines);

  const renderOrderPrintSectionSubtotalFoot = (label, amount, layout) =>
    layout.hideFinancials ? null : (
      <tfoot>
        <tr>
          <td colSpan={layout.subtotalLabelColSpan} style={{ textAlign: 'right' }}>
            {label}
          </td>
          <td style={{ textAlign: 'right' }}>{formatCurrency(amount)}</td>
        </tr>
      </tfoot>
    );

  const orderPrintLayoutStandard = getOrderPrintLineLayout(false);
  const orderPrintLayoutShop = getOrderPrintLineLayout(true);

  const renderWorkOrderPrintInner = (layout) => (
    <>
        <div className="print-quote-recipient" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 200, flex: '1 1 200px' }}>
            <div style={{ marginBottom: 4 }}>
              <strong>Bill To</strong>
            </div>
            <div className="print-quote-to-address">
              <div>{order.company_name || `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() || 'Customer'}</div>
              {(order.billing_address_line1 || order.address_line1) && <div>{order.billing_address_line1 || order.address_line1}</div>}
              {(order.billing_address_line2 || order.address_line2) && <div>{order.billing_address_line2 || order.address_line2}</div>}
              {((order.billing_city || order.city) || (order.billing_state || order.state) || (order.billing_zip || order.zip_code)) && (
                <div>
                  {(order.billing_city || order.city) ? `${order.billing_city || order.city}, ` : ''}
                  {order.billing_state || order.state || ''} {order.billing_zip || order.zip_code || ''}
                </div>
              )}
            </div>
          </div>
          <div style={{ minWidth: 200, flex: '1 1 200px' }}>
            <div style={{ marginBottom: 4 }}>
              <strong>Ship To</strong>
            </div>
            <div className="print-quote-to-address">
              {order.shipping_address_line1 ? (
                <>
                  <div>{order.shipping_address_line1}</div>
                  {order.shipping_address_line2 && <div>{order.shipping_address_line2}</div>}
                  {(order.shipping_city || order.shipping_state || order.shipping_zip) && (
                    <div>
                      {order.shipping_city ? `${order.shipping_city}, ` : ''}
                      {order.shipping_state || ''} {order.shipping_zip || ''}
                    </div>
                  )}
                  {order.shipping_country && <div>{order.shipping_country}</div>}
                </>
              ) : (
                <div style={{ color: '#666' }}>—</div>
              )}
            </div>
          </div>
        </div>

        <div
          className="print-quote-details-box"
          style={{
            marginBottom: 16,
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 0
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'flex-start',
              gap: 24,
              width: '100%'
            }}
          >
            <div className="print-quote-details-left">
              <div><strong>Order #:</strong> {order.identifier || order.work_order_number}</div>
              {orderTypeLabel(order) ? (
                <div>
                  <strong>Type:</strong> {orderTypeLabel(order)}
                </div>
              ) : null}
              <div>
                <strong>Order Date:</strong>{' '}
                {order.order_date ? formatDateOnly(order.order_date, 'MM/dd/yyyy') : 'N/A'}
              </div>
              {order.po_number && (
                <div>
                  <strong>PO #:</strong> {order.po_number}
                </div>
              )}
            </div>
            {(order.due_date || order.quote_number != null) ? (
              <div className="print-quote-details-center">
                {order.due_date && (
                  <div>
                    <strong>Due Date:</strong> {formatDateOnly(order.due_date, 'MM/dd/yyyy')}
                  </div>
                )}
                {order.quote_number != null && (
                  <div>
                    <strong>Quote #:</strong> {formatOrderQuoteNumber(order)}
                  </div>
                )}
              </div>
            ) : null}
            <div className="print-quote-details-right">
              <div><strong>Status:</strong> {order.status?.toUpperCase() || 'OPEN'}</div>
              <div>
                <strong>Customer:</strong>{' '}
                {order.company_name || `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() || 'Customer'}
              </div>
            </div>
          </div>
          {String(order.notes ?? '').trim() && !printLayoutHideComments ? (
            <div
              style={{
                width: '100%',
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #ccc',
                textAlign: 'left'
              }}
            >
              <strong>Comments:</strong>
              <div style={{ fontWeight: 'normal', whiteSpace: 'pre-wrap', marginTop: 4 }}>
                {String(order.notes).trim()}
              </div>
            </div>
          ) : null}
        </div>

        {isScopeOfWorkOrder(order) ? (
          <FieldWorkOrderPrintSection
            order={order}
            hideDescriptionBlock={printLayoutHideDescription}
            omitFinancial={layout.hideFinancials}
          />
        ) : order.description && !printLayoutHideDescription ? (
          <div className="print-section" style={{ marginBottom: '20px', marginTop: '10px' }}>
            <strong>Description:</strong> {order.description}
          </div>
        ) : null}

        {!isScopeOfWorkOrder(order) ? (
          !printLayoutSeparateLineSections ? (
            <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              {renderOrderPrintLineTableHead(layout)}
              <tbody>
                {order.lineItems && order.lineItems.length > 0 ? (
                  renderOrderPrintLineRows([...partLines, ...serviceLines], layout)
                ) : (
                  <tr>
                    <td colSpan={layout.lineColCount} style={{ border: '1px solid #000', padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>No line items</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="print-order-line-sections-wrap" style={{ marginBottom: '20px' }}>
              {order.lineItems && order.lineItems.length > 0 ? (
                <>
                  {partLines.length > 0 ? (
                    <div className="print-order-line-section">
                      <div className="print-order-line-section-title">Parts and Materials</div>
                      <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        {renderOrderPrintLineTableHead(layout)}
                        <tbody>{renderOrderPrintLineRows(partLines, layout)}</tbody>
                        {renderOrderPrintSectionSubtotalFoot('Parts and Materials total', orderPrintPartsSubtotal, layout)}
                      </table>
                    </div>
                  ) : null}
                  {serviceLines.length > 0 ? (
                    <div className={`print-order-line-section${partLines.length > 0 ? ' print-order-line-section--labor' : ''}`}>
                      <div className="print-order-line-section-title">Labor and Services</div>
                      <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        {renderOrderPrintLineTableHead(layout)}
                        <tbody>{renderOrderPrintLineRows(serviceLines, layout)}</tbody>
                        {renderOrderPrintSectionSubtotalFoot('Labor and Services total', orderPrintLaborSubtotal, layout)}
                      </table>
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <div className="print-order-line-section">
                    <div className="print-order-line-section-title">Parts and Materials</div>
                    <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      {renderOrderPrintLineTableHead(layout)}
                      <tbody>{renderOrderPrintEmptyBodyRow(layout)}</tbody>
                      {renderOrderPrintSectionSubtotalFoot('Parts and Materials total', 0, layout)}
                    </table>
                  </div>
                  <div className="print-order-line-section print-order-line-section--labor">
                    <div className="print-order-line-section-title">Labor and Services</div>
                    <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      {renderOrderPrintLineTableHead(layout)}
                      <tbody>{renderOrderPrintEmptyBodyRow(layout)}</tbody>
                      {renderOrderPrintSectionSubtotalFoot('Labor and Services total', 0, layout)}
                    </table>
                  </div>
                </>
              )}
            </div>
          )
        ) : null}

        {/* Field Work prints already include Total proposal amount via FieldWorkOrderPrintSection */}
        {!layout.hideFinancials && !isScopeOfWorkOrder(order) ? (
          <div style={{ marginTop: '20px', marginLeft: 'auto', width: '300px', borderTop: '2px solid #000', paddingTop: '10px' }}>
            {order.line_subtotal != null && order.tax_amount != null ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                  <span>Line items subtotal:</span>
                  <span>{formatCurrency(order.line_subtotal, '0')}</span>
                </div>
                {(Number(order.discount_amount) > 0.005 || orderDisplayDiscount > 0.005) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                    <span>Discount:</span>
                    <span>
                      −
                      {formatCurrency(
                        Number(order.discount_amount) > 0.005
                          ? order.discount_amount
                          : orderDisplayDiscount,
                        '0'
                      )}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                  <span>Tax:</span>
                  <span>{formatCurrency(order.tax_amount, '0')}</span>
                </div>
              </>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
              <span>Total:</span>
              <span>{formatCurrency(order.total_amount || 0)}</span>
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: '40px', paddingTop: '10px', borderTop: '1px solid #ccc', fontSize: '10px', color: '#666', textAlign: 'center' }}>
          <div>Thank you for your business!</div>
          <div>Generated on {format(new Date(), 'MM/dd/yyyy hh:mm a')}</div>
        </div>
    </>
  );

  return (
    <>
      {/* Print-friendly views — standard vs shop-docs (body class toggles visibility) */}
      <PrintPage title="Work Order" rootClassName="print-work-order-standard">
        {renderWorkOrderPrintInner(orderPrintLayoutStandard)}
      </PrintPage>
      <PrintPage
        title={`Shop Doc for Order ${order.identifier || order.work_order_number || id}`}
        rootClassName="print-work-order-shop-docs"
      >
        {renderWorkOrderPrintInner(orderPrintLayoutShop)}
        {shopDocPrintAssets && shopDocPrintAssets.length > 0
          ? shopDocPrintAssets.map((asset, idx) => (
              <div
                key={asset.id}
                className={idx > 0 ? 'print-page-break' : undefined}
                style={{ marginTop: idx > 0 ? 24 : 16 }}
              >
                <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: 8 }}>Shop document: {asset.file_name}</div>
                {(asset.slides || []).map((slide, sidx) =>
                  slide.unsupported ? (
                    <div key={sidx} style={{ fontSize: '11px', color: '#444', marginBottom: 8 }}>
                      Preview is not available for this file in print (or the PDF could not be rendered). Open the
                      attachment in Work Manager to view it.
                    </div>
                  ) : (
                    <div
                      key={sidx}
                      className={sidx > 0 ? 'print-page-break' : undefined}
                      style={{ marginTop: sidx > 0 ? 12 : 0 }}
                    >
                      <div className="print-shop-doc-embed">
                        <img src={slide.src} alt="" />
                      </div>
                    </div>
                  )
                )}
              </div>
            ))
          : null}
      </PrintPage>

      {/* Regular view */}
      <Box className="no-print">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/work-orders')} size="small" sx={{ mb: 0.5 }}>
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Order: {order.identifier || order.work_order_number}
            </Typography>
            {revisionDialogItems.length > 0 && (
              <Tooltip title="View previous revisions">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setRevisionDialogOpen(true)}
                  aria-label="View previous order revisions"
                  sx={{ mt: 0.25 }}
                >
                  <TextSnippet fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box>
            <>
              {editing ? (
                <>
                  <IconButton size="small" color="primary" title="Save" onClick={handleSaveOrderEdit} disabled={saving}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Cancel" onClick={handleCancelOrderEdit} disabled={saving}>
                    <Cancel fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  title="Edit"
                  onClick={handleEditOrder}
                  disabled={isHistoricalOrder || orderLockedByOther || editLockBusy}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color="error"
                title="Delete"
                onClick={handleDeleteOrder}
                disabled={editing || isHistoricalOrder || loadingDeleteDependencies || hasPackingSlipDependency || orderInvoices.length > 0 || !!order?.customer_invoice_id}
              >
                <Delete fontSize="small" />
              </IconButton>
              <Tooltip title="Copy to new order">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Copy to new order"
                    onClick={() => setCopyOrderDialogOpen(true)}
                    disabled={editing || copyOrderBusy}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Print settings">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setPrintSettingsDraft({
                      hideComments: true,
                      hideDescription: true,
                      separateLineSections: false
                    });
                    setOrderPrintSettingsOpen(true);
                  }}
                  aria-label="Print settings"
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" color="primary" title="Print" onClick={handlePrint}>
                <Print fontSize="small" />
              </IconButton>
              <Tooltip title="Print shop package: work order without pricing, plus attachments tagged Shop">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label="Shop docs print"
                    onClick={handlePrintShopDocs}
                    disabled={shopDocPrintBusy}
                  >
                    {shopDocPrintBusy ? <CircularProgress size={18} thickness={5} /> : <Article fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              {!isHistoricalOrder && id && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  sx={{ ml: 0.5 }}
                  disabled={invoiceDraftLoading}
                  onClick={openCreateInvoiceDraft}
                >
                  {invoiceDraftLoading ? 'Loading…' : 'Create invoice'}
                </Button>
              )}
              {!isHistoricalOrder && id && (Number(orderInvoiceFinancialTotals?.outstanding) || 0) > 0.02 && order?.customer_id ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  sx={{ ml: 0.5 }}
                  onClick={() => setRecordPaymentOpen(true)}
                >
                  Record Payment
                </Button>
              ) : null}
              <Button size="small" variant="outlined" sx={{ ml: 0.5 }} onClick={() => navigate(`/packing-slips?work_order_id=${id}`)}>
                Packing slips
              </Button>
              <Button size="small" variant="outlined" sx={{ ml: 0.5 }} onClick={handleOpenCreatePackingSlip}>
                Create packing slip
              </Button>
            </>
        </Box>
      </Box>

      {orderLockedByOther && !editing ? (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            isAdmin() ? (
              <Button color="inherit" size="small" onClick={handleForceClearEditLock}>
                Clear edit lock
              </Button>
            ) : null
          }
        >
          This order is being edited by <strong>{editLockDisplayName(order.edit_lock)}</strong>. Pencil edit is unavailable
          until they finish, an administrator clears the lock, or the lock expires
          {order.edit_lock?.expires_at
            ? ` (around ${format(new Date(order.edit_lock.expires_at), 'MMM d, h:mm a')})`
            : ''}
          .
        </Alert>
      ) : null}

      {/* Order Information Bar Header */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.identifier || order.work_order_number || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Type</Typography>
            {orderTypeLabel(order) ? (
              <Chip
                label={orderTypeLabel(order)}
                size="small"
                sx={isTmOrder(order) ? tmChipSx : scopeOfWorkChipSx}
              />
            ) : (
              <Typography variant="body2" sx={{ minHeight: 24 }} />
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order Date</Typography>
            <Typography variant="body2">{order.order_date ? formatDateOnly(order.order_date, 'MM/dd/yyyy') : '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{order.po_number || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {order.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${order.customer_id}`)}
                >
                  {order.company_name || [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') || order.customer_number || `#${order.customer_id}`}
                </Button>
              ) : (
                '-'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(orderDisplayGrandTotal, '-')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Amt Paid</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(orderInvoiceFinancialTotals.amountPaidTotal, '-')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quoted By</Typography>
            <Typography variant="body2">
              {order.salesman_first_name && order.salesman_last_name ? `${order.salesman_first_name} ${order.salesman_last_name}` : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
              Reference Quote
            </Typography>
            <Typography variant="body2">
              {referenceQuoteIdentifier ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={navigateToReferenceQuote}
                >
                  {referenceQuoteIdentifier}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box sx={{ px: 1, py: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Quote #</Typography>
            <Typography variant="body2">
              {order.quote_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/quotes/${order.quote_id}`)}
                >
                  {formatOrderQuoteNumber(order)}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          {order.quote_id ? (
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Order / quote revised
              </Typography>
              <Typography variant="body2">
                {order.order_last_edited_at
                  ? `${format(new Date(order.order_last_edited_at), 'MM/dd/yyyy h:mm a')} · ${
                      [order.order_last_edited_by_first_name, order.order_last_edited_by_last_name].filter(Boolean).join(' ') ||
                      '—'
                    }`
                  : '—'}
              </Typography>
            </Box>
          ) : null}
          <Box className="wm-summary-compound">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoices</Typography>
              <Typography component="div" variant="body2">
                {summaryInvoicesByCreatedAsc.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, mt: 0.25 }}>
                    {summaryInvoicesByCreatedAsc.map((inv) => (
                      <Button
                        key={inv.id}
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                        onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                      >
                        {inv.identifier || inv.invoice_number || `#${inv.id}`}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  '-'
                )}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slips</Typography>
              <Typography component="div" variant="body2">
                {summaryPackingSlipsByCreatedAsc.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, mt: 0.25 }}>
                    {summaryPackingSlipsByCreatedAsc.map((slip) => (
                      <Button
                        key={slip.id}
                        size="small"
                        variant="text"
                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                        onClick={() => navigate(`/packing-slips/${slip.id}`)}
                      >
                        {slip.identifier || slip.packing_slip_number || `#${slip.id}`}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  '-'
                )}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0, maxWidth: 320 }}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>
                Linked POs
              </Typography>
              <Typography component="div" variant="body2">
                {linkedPurchaseOrdersForOrder.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, mt: 0.25 }}>
                    {linkedPurchaseOrdersForOrder.map((lpo) => {
                      const poLabel = lpo.po_identifier || lpo.po_number || `#${lpo.purchase_order_id}`;
                      return (
                        <Box key={lpo.purchase_order_id} sx={{ maxWidth: '100%' }}>
                          <Button
                            size="small"
                            variant="text"
                            sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                            onClick={() => navigate(`/procurement/po/${lpo.purchase_order_id}`)}
                          >
                            {poLabel}
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  '-'
                )}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            <Chip label={order.status || '-'} size="small" sx={getStatusChipSx(order.status)} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{order.due_date ? formatDateOnly(order.due_date, 'MM/dd/yyyy') : '-'}</Typography>
          </Box>
        </Box>
        {linkedPurchaseOrdersForOrder.length > 0 ? (
          <Alert severity="info" sx={{ mt: 1.25, width: '100%', boxSizing: 'border-box' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Order-linked purchase orders
            </Typography>
            <Typography variant="body2" component="div">
              This job has one or more purchase orders created from the order.
            </Typography>
          </Alert>
        ) : null}
      </Paper>

      {isScopeOfWorkOrder(order) ? (
        <FieldWorkOrderDetailCard
          order={order}
          editing={editing}
          editedOrder={editedOrder}
          readOnly={isHistoricalOrder || orderLockedByOther}
          onPatch={(patch) => setEditedOrder((prev) => (prev ? { ...prev, ...patch } : prev))}
        />
      ) : null}

      {!isScopeOfWorkOrder(order) ? (
        <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main', minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Description
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.5,
              mb: 1.5,
              maxWidth: '100%',
            }}
          >
            {String(order.description || '').trim() ? order.description : '—'}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Comments
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.5,
              maxWidth: '100%',
            }}
          >
            {String(order.notes || '').trim() ? order.notes : '—'}
          </Typography>
        </Paper>
      ) : null}

      {!isHistoricalOrder && (
        <>
          {completionReadiness && (
            <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#fafafa', border: '1px solid #e0e0e0' }}>
              {completionReadiness.ready ? (
                <Chip size="small" label="Ready to complete" color="success" variant="outlined" />
              ) : (
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Order is{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {formatWorkOrderStatusForCompletionBanner(order.status)}
                  </Box>
                  .
                  {completionAwaitingItems.length > 0 ? (
                    <>
                      {' '}
                      Awaiting{' '}
                      {completionAwaitingItems.map((item, idx) => (
                        <Fragment key={item.key}>
                          {idx > 0 &&
                            (idx === completionAwaitingItems.length - 1 ? (
                              <> and </>
                            ) : (
                              <>, </>
                            ))}
                          {item.action === 'tab' ? (
                            <Button
                              type="button"
                              variant="text"
                              size="small"
                              onClick={() => setActiveTab(item.tab)}
                              sx={completionBannerLinkButtonSx}
                            >
                              {item.label}
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="text"
                              size="small"
                              onClick={() =>
                                thirdPartySubcontractRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start'
                                })
                              }
                              sx={completionBannerLinkButtonSx}
                            >
                              {item.label}
                            </Button>
                          )}
                        </Fragment>
                      ))}
                      .
                    </>
                  ) : (
                    <> Awaiting further steps before completion.</>
                  )}
                </Typography>
              )}
            </Paper>
          )}
          <Paper
            ref={thirdPartySubcontractRef}
            sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
          >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.9rem' }}>
            Third party / subcontract
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, maxWidth: 960, lineHeight: 1.45 }}>
            <strong>Order purchase orders:</strong> <strong>New PO for order</strong> builds a PO whose lines are tied to this
            job. Use it to record vendor pricing against the order; customer billing runs through <strong>Create invoice</strong>{' '}
            in the header above, not through procurement&apos;s vendor-invoice / received-shipment flow.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
            <Tooltip
              title="Creates a PO linked to this order. Bill the customer with Create invoice (customer invoices), not Shipment received → Vendor invoice."
              arrow
            >
              <Button size="small" variant="outlined" onClick={() => navigate(`/procurement/po/new?work_order_id=${id}`)}>
                New PO for order
              </Button>
            </Tooltip>
          </Box>
          {linkedPurchaseOrdersForOrder.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Linked purchase orders (vendor receipt status)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'stretch', maxWidth: 560 }}>
                {linkedPurchaseOrdersForOrder.map((lpo) => {
                  const poLabel = lpo.po_identifier || lpo.po_number || `#${lpo.purchase_order_id}`;
                  const pres = getLinkedPoStatusPresentation(lpo.po_status);
                  const metaBits = [];
                  if (lpo.vendor_name) metaBits.push(String(lpo.vendor_name).trim());
                  if (lpo.order_date) metaBits.push(`PO date ${formatDateOnly(lpo.order_date, 'MM/dd/yyyy')}`);
                  if (lpo.expected_date) metaBits.push(`Expected ${formatDateOnly(lpo.expected_date, 'MM/dd/yyyy')}`);
                  return (
                    <Paper key={lpo.purchase_order_id} variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Button
                          size="small"
                          variant="text"
                          sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 600 }}
                          onClick={() => navigate(`/procurement/po/${lpo.purchase_order_id}`)}
                        >
                          {poLabel}
                        </Button>
                        <Tooltip title={`Linked PO: ${pres.label}`} arrow>
                          <span>
                            <Chip label={pres.label} color={pres.color} size="small" sx={{ fontWeight: 500 }} />
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: 'auto', flexShrink: 0, textTransform: 'none' }}
                          onClick={() =>
                            navigate(
                              `/packing-slips/new?work_order_id=${id}&shipment_purpose=vendor_outbound&linked_purchase_order_id=${lpo.purchase_order_id}`
                            )
                          }
                        >
                          Vendor shipment slip
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {pres.label}
                        </Box>
                        {metaBits.length > 0 ? ` · ${metaBits.join(' · ')}` : null}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}
        </Paper>
        </>
      )}

      {isHistoricalOrder && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This Order was modified {formatModifiedTimestamp(order.order_last_edited_at || order.superseded_at)} by{' '}
          {[order.order_last_edited_by_first_name, order.order_last_edited_by_last_name].filter(Boolean).join(' ') || '—'}. The next version of this Order is{' '}
          {order?.superseded_by_work_order_id ? (
            <Button
              color="inherit"
              size="small"
              variant="text"
              onClick={() => navigate(`/work-orders/${order.superseded_by_work_order_id}`)}
              sx={{ textTransform: 'none', ml: 0.75, px: 0.75 }}
            >
              {order.superseded_by_work_order_identifier ?? order.superseded_by_work_order_number ?? order.superseded_by_work_order_id}
            </Button>
          ) : (
            '—'
          )}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 1.5 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="General" />
          <Tab label="Work Hrs" />
          {!isScopeOfWorkOrder(order) ? <Tab label={`Line Items ${order.lineItems?.length || 0}`} /> : null}
          <Tab label="Shipping and Fulfillment" />
          <Tab label="Addresses" />
          <Tab label="Comments" />
          <Tab label="Shipments" />
          <Tab label={`Invoices ${orderInvoices.length || 0}`} />
          <Tab label="Lineage" />
          <Tab label={`Attachments ${attachments.length || 0}`} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === orderDetailTabs.general && (
        <Box>
          {/* General Info */}
          <Card sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75, fontSize: '0.8125rem' }}>
                General
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Order Number"
                    value={order.identifier || order.work_order_number || ''}
                    disabled
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Customer PO"
                    value={editing && editedOrder ? (editedOrder.po_number ?? '') : (order.po_number || '')}
                    onChange={handleOrderFieldChange('po_number')}
                    disabled={!editing}
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={
                      order.company_name ||
                      `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() ||
                      (order.customer_id ? 'Customer not found (may have been deleted)' : '')
                    }
                    disabled
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={editing && editedOrder ? editedOrder.description : (order.description || '')}
                    onChange={handleOrderFieldChange('description')}
                    disabled={!editing}
                    margin="none"
                    size="small"
                    multiline
                    rows={2}
                    helperText={
                      isScopeOfWorkOrder(order)
                        ? 'Optional order summary. Scope of work and proposal amounts are edited in the Field Work / Service card above.'
                        : undefined
                    }
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={{
                      ...orderDenseFieldSx,
                      '& .MuiOutlinedInput-input': {
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        padding: '6px 10px'
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Comments"
                    value={editing && editedOrder ? editedOrder.notes : (order.notes || '')}
                    onChange={handleOrderFieldChange('notes')}
                    disabled={!editing}
                    margin="none"
                    size="small"
                    multiline
                    rows={3}
                    placeholder="Order-level comments"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={{
                      ...orderDenseFieldSx,
                      mt: '8px',
                      '& .MuiOutlinedInput-input': {
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        padding: '6px 10px'
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Order Total"
                    value={formatCurrency(orderDisplayGrandTotal, '')}
                    disabled
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Order Date"
                    type="date"
                    value={editing && editedOrder ? editedOrder.order_date : formatDateForInput(order.order_date)}
                    onChange={handleOrderFieldChange('order_date')}
                    disabled={!editing}
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    value={editing && editedOrder ? editedOrder.due_date : formatDateForInput(order.due_date)}
                    onChange={handleOrderFieldChange('due_date')}
                    disabled={!editing}
                    margin="none"
                    size="small"
                    InputLabelProps={orderDenseInputLabelProps}
                    sx={orderDenseFieldSx}
                  />
                  {editing && (
                    <FormControl
                      fullWidth
                      margin="none"
                      size="small"
                      sx={{
                        mt: '8px',
                        mb: 0.65,
                        display: 'block'
                      }}
                    >
                      <InputLabel id="order-status-dense" shrink>
                        Status
                      </InputLabel>
                      <Select
                        labelId="order-status-dense"
                        value={editedOrder?.status || order.status || 'open'}
                        onChange={(e) => setEditedOrder({ ...editedOrder, status: e.target.value })}
                        label="Status"
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="waiting_external">Waiting External</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="Partial">Partial</MenuItem>
                        <MenuItem value="Ready to Ship">Ready to Ship</MenuItem>
                        <MenuItem value="shipped">Shipped</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              </Grid>
              {orderEditSaveBar ? <Box sx={{ mt: 1.25 }}>{orderEditSaveBar}</Box> : null}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === orderDetailTabs.workHrs && (
        <WorkHoursTab
          orderId={order.id}
          order={order}
          dueDate={order.due_date}
          readOnly={isHistoricalOrder || orderLockedByOther}
        />
      )}

      {orderDetailTabs.lineItems != null && activeTab === orderDetailTabs.lineItems && (
        <Box>
          {/* Line items */}
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              {orderEditSaveBar ? <Box sx={{ mb: 1.5 }}>{orderEditSaveBar}</Box> : null}
              {!isHistoricalOrder && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleAddLineItemDraft('part')}
                    disabled={saving || !editing}
                  >
                    + Add Part
                  </Button>
                  {!editing && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                      Click Edit in the header to enable line-item changes.
                    </Typography>
                  )}
                </Box>
              )}
              <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table size="small" sx={orderTmQuoteStyleLineItemTableSx}>
                  <TableBody>
                    {(order.lineItems && order.lineItems.length > 0) || newLineItems.length > 0 || !isHistoricalOrder ? (
                      <>
                        <TableRow>
                          <TableCell colSpan={ORDER_TM_LINE_TABLE_COLS} sx={{ border: '1px solid #e0e0e0', bgcolor: '#50a8ee', fontWeight: 700 }}>
                            Parts and Materials
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="center" sx={{ ...orderTmLineHdrCellSx, px: 0.5, width: 18 }}>Item</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Code</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Part</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Qty</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Unit</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Price</TableCell>
                          <TableCell
                            align="right"
                            sx={{ ...orderTmLineHdrCellSx, px: 0.35, fontSize: '0.7rem', width: 88, minWidth: 88 }}
                          >
                            Tax %
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ ...orderTmLineHdrCellSx, textAlign: 'right', width: 128, minWidth: 128 }}
                          >
                            Tax $
                          </TableCell>
                          <TableCell align="center" sx={{ ...orderTmLineHdrCellSx, textAlign: 'right' }}>
                            {isPerLineOrderTax ? 'Line total' : 'Subtotal'}
                          </TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Status</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Notes</TableCell>
                          <TableCell align="center" sx={orderTmLineHdrCellSx}>Actions</TableCell>
                        </TableRow>
                        {partLines.length === 0 && newPartDraftRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={ORDER_TM_LINE_TABLE_COLS}
                              sx={{
                                ...orderTmLineBodyCellSx,
                                textAlign: 'center',
                                py: 2,
                                color: 'text.secondary',
                                fontSize: '0.8125rem',
                                fontStyle: 'italic'
                              }}
                            >
                              No part lines yet
                            </TableCell>
                          </TableRow>
                        ) : null}
                        {partLines.map((item, pIdx) => {
                        const isEditing = String(editingItemId) === String(item.id);
                        const editedItem = editedLineItems[item.id] || item;
                        const partNumber = isEditing
                          ? (editedItem.part_number ?? item.part_number ?? '')
                          : (item.part_number ?? '');
                        const fs = getLineFulfillmentStatus(item);
                        const lineSub = orderLineSubtotalColumnAmount(isEditing ? editedItem : item);

                        return (
                          <TableRow key={item.id}>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                              <Box sx={orderTmLineItemIdxBoxSx}>{pIdx + 1}</Box>
                            </TableCell>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                              <Box sx={orderTmLineItemIdxBoxSx}>{partNumber || '—'}</Box>
                            </TableCell>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                              {isEditing ? (
                                <OrderLineMaterialsRefAutocomplete
                                  editedItem={editedItem}
                                  onCommit={(v, record) => handleUpdateLineItemField(item.id, 'item_ref', v, record)}
                                  label="Part / service"
                                  noMargin
                                  textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                                />
                              ) : (
                                item.part_name ?? item.description ?? ''
                              )}
                            </TableCell>
                            <TableCell sx={orderTmLineBodyCellSx}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={formatLineItemQtyEditValue('part', editedItem.quantity_required)}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'quantity_required', e.target.value)}
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              ) : (
                                formatQuantityDisplay(item.quantity_required ?? 0, { itemType: 'part' })
                              )}
                            </TableCell>
                            <TableCell sx={orderTmLineBodyCellSx}>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editedItem.unit || 'EA'}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'unit', e.target.value)}
                                />
                              ) : (
                                item.unit || 'EA'
                              )}
                            </TableCell>
                            <TableCell sx={orderTmLineBodyCellSx}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={editedItem.unit_price != null ? editedItem.unit_price : ''}
                                  onChange={(e) => handleUpdateLineItemField(item.id, 'unit_price', e.target.value)}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              ) : (
                                formatCurrency(item.unit_price != null ? item.unit_price : 0)
                              )}
                            </TableCell>
                            {renderOrderLineTaxCells({
                              row: isEditing ? editedItem : item,
                              isEditing,
                              disabled: saving || !canEditLineItems,
                              onLineTaxPatch: (patch) => handleApplyOrderLineTaxPatch(item.id, patch)
                            })}
                            <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'right' }}>
                              {formatCurrency(lineSub, '$0.00')}
                            </TableCell>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                              <Tooltip title="Line fulfillment (packing slips and ship dates)">
                                <Chip label={fs.label} size="small" color={fs.color} sx={{ maxWidth: '100%' }} />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isEditing ? (
                                  <>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={editedItem.notes ?? ''}
                                      onChange={(e) => handleUpdateLineItemField(item.id, 'notes', e.target.value)}
                                      placeholder="Notes"
                                    />
                                    {String(editedItem.notes ?? '').trim() !== '' && (
                                      <Tooltip title="Open full notes">
                                        <IconButton
                                          size="small"
                                          onClick={() => openLineTextPopout('Line item notes', editedItem.notes ?? '')}
                                          aria-label="Open full line item notes"
                                        >
                                          <OpenInNew fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Typography
                                      variant="inherit"
                                      component="span"
                                      sx={{
                                        lineHeight: 1.2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        flex: 1,
                                        minWidth: 0
                                      }}
                                    >
                                      {item.notes || '—'}
                                    </Typography>
                                    {!!(item.notes && String(item.notes).trim()) && (
                                      <Tooltip title="Open full notes">
                                        <IconButton
                                          size="small"
                                          onClick={() => openLineTextPopout('Line item notes', item.notes)}
                                          aria-label="Open full line item notes"
                                        >
                                          <OpenInNew fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                              {isEditing ? (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleSaveLineItem(item.id)}
                                    disabled={saving}
                                    title="Save"
                                  >
                                    <Save fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEditLineItem}
                                    disabled={saving}
                                    title="Cancel"
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!canEditLineItems}
                                    title={canEditLineItems ? 'Edit line' : 'Edit order header to change line items'}
                                    onClick={() => canEditLineItems && handleStartEditLineItem(item)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <Tooltip
                                    title={
                                      item.on_packing_slip
                                        ? 'This line is on a packing slip and cannot be removed.'
                                        : 'Delete'
                                    }
                                  >
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={!canEditLineItems || item.on_packing_slip}
                                        aria-label="Delete line item"
                                        onClick={() => canEditLineItems && !item.on_packing_slip && handleRequestDeleteLineItem(item.id)}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {newPartDraftRows.map(({ item, index: draftIndex }, dIdx) => {
                      const newCode = item.part_id ? (item.part_number ?? '') : '';
                      return (
                        <TableRow key={`new-part-${draftIndex}`}>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{partLines.length + dIdx + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{newCode || '—'}</Box>
                          </TableCell>
                          <TableCell
                            className="wm-order-new-draft-part-cell"
                            sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}
                          >
                            <LineItemPartAutocomplete
                              valueId={item.part_id}
                              onCommit={(v, part) => handleLineItemChange(draftIndex, 'item_ref', v, part)}
                              label="Part"
                              noMargin
                              textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity_required}
                              onChange={(e) => handleLineItemChange(draftIndex, 'quantity_required', e.target.value)}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              size="small"
                              value={item.unit || 'EA'}
                              onChange={(e) => handleLineItemChange(draftIndex, 'unit', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price}
                              onChange={(e) => handleLineItemChange(draftIndex, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          {renderOrderLineTaxCells({
                            row: item,
                            isEditing: true,
                            disabled: saving || !editing,
                            onLineTaxPatch: (patch) => handleNewOrderLineTaxPatch(draftIndex, patch)
                          })}
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'right' }}>
                            {formatCurrency(orderLineSubtotalColumnAmount(item), '$0.00')}
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.notes || ''}
                                onChange={(e) => handleLineItemChange(draftIndex, 'notes', e.target.value)}
                                placeholder="Notes"
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                              {String(item.notes || '').trim() !== '' && (
                                <Tooltip title="Open full notes">
                                  <IconButton
                                    size="small"
                                    onClick={() => openLineTextPopout('Line item notes', item.notes || '')}
                                    aria-label="Open full line item notes"
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(draftIndex)} title="Remove">
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={ORDER_TM_LINE_TABLE_COLS} sx={{ border: 0, height: 14, bgcolor: '#ffffff', p: 0 }} />
                    </TableRow>
                    {!isHistoricalOrder && (
                      <TableRow>
                        <TableCell colSpan={ORDER_TM_LINE_TABLE_COLS} sx={{ border: 0, p: 0.5, bgcolor: 'transparent' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              startIcon={<AddIcon />}
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleAddLineItemDraft('service')}
                              disabled={saving || !editing}
                            >
                              + Add Service
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell colSpan={ORDER_TM_LINE_TABLE_COLS} sx={{ border: '1px solid #e0e0e0', borderTop: '8px solid #ffffff', bgcolor: '#50a8ee', fontWeight: 700 }}>
                        Labor and Services
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="center" sx={{ ...orderTmLineHdrCellSx, px: 0.5, width: 18 }}>Item</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Code</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Service</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Qty</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Unit</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Price</TableCell>
                      <TableCell
                        align="right"
                        sx={{ ...orderTmLineHdrCellSx, px: 0.35, fontSize: '0.7rem', width: 88, minWidth: 88 }}
                      >
                        Tax %
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ ...orderTmLineHdrCellSx, textAlign: 'right', width: 128, minWidth: 128 }}
                      >
                        Tax $
                      </TableCell>
                      <TableCell align="center" sx={{ ...orderTmLineHdrCellSx, textAlign: 'right' }}>
                        {isPerLineOrderTax ? 'Line total' : 'Subtotal'}
                      </TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Status</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Notes</TableCell>
                      <TableCell align="center" sx={orderTmLineHdrCellSx}>Actions</TableCell>
                    </TableRow>
                    {serviceLines.length === 0 && newServiceDraftRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={ORDER_TM_LINE_TABLE_COLS}
                          sx={{
                            ...orderTmLineBodyCellSx,
                            textAlign: 'center',
                            py: 2,
                            color: 'text.secondary',
                            fontSize: '0.8125rem',
                            fontStyle: 'italic'
                          }}
                        >
                          No labor or service lines yet
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {serviceLines.map((item, sIdx) => {
                      const isEditing = String(editingItemId) === String(item.id);
                      const editedItem = editedLineItems[item.id] || item;
                      const code = effectiveServiceCode(
                        isEditing
                          ? { service_code: editedItem.service_code, id: editedItem.service_id }
                          : { service_code: item.service_code, id: item.service_id }
                      );
                      const fs = getLineFulfillmentStatus(item);
                      const lineSub = orderLineSubtotalColumnAmount(isEditing ? editedItem : item);
                      return (
                        <TableRow key={`svc-${item.id}`}>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{partLines.length + sIdx + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{code || '—'}</Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                            {isEditing ? (
                              <LineItemServiceAutocomplete
                                valueId={editedItem.service_id}
                                onCommit={(v, svc) => handleUpdateLineItemField(item.id, 'item_ref', v, svc)}
                                label="Service"
                                showNewService
                                noMargin
                                textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                              />
                            ) : (
                              item.service_name ?? item.description ?? ''
                            )}
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            {isEditing ? (
                              <TextField
                                type="text"
                                inputMode="decimal"
                                size="small"
                                value={formatLineItemQtyEditValue('service', editedItem.quantity_required)}
                                onChange={(e) => handleUpdateLineItemField(item.id, 'quantity_required', e.target.value)}
                              />
                            ) : (
                              formatQuantityDisplay(item.quantity_required ?? 0, { itemType: 'service' })
                            )}
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            {isEditing ? (
                              <TextField
                                size="small"
                                value={editedItem.unit || 'HR'}
                                onChange={(e) => handleUpdateLineItemField(item.id, 'unit', e.target.value)}
                              />
                            ) : (
                              item.unit || 'HR'
                            )}
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            {isEditing ? (
                              <TextField
                                type="number"
                                size="small"
                                value={editedItem.unit_price != null ? editedItem.unit_price : ''}
                                onChange={(e) => handleUpdateLineItemField(item.id, 'unit_price', e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                              />
                            ) : (
                              formatCurrency(item.unit_price != null ? item.unit_price : 0)
                            )}
                          </TableCell>
                          {renderOrderLineTaxCells({
                            row: isEditing ? editedItem : item,
                            isEditing,
                            disabled: saving || !canEditLineItems,
                            onLineTaxPatch: (patch) => handleApplyOrderLineTaxPatch(item.id, patch)
                          })}
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'right' }}>
                            {formatCurrency(lineSub, '$0.00')}
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Tooltip title="Line fulfillment (packing slips and ship dates)">
                              <Chip label={fs.label} size="small" color={fs.color} sx={{ maxWidth: '100%' }} />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {isEditing ? (
                                <>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    value={editedItem.notes ?? ''}
                                    onChange={(e) => handleUpdateLineItemField(item.id, 'notes', e.target.value)}
                                    placeholder="Notes"
                                  />
                                  {String(editedItem.notes ?? '').trim() !== '' && (
                                    <Tooltip title="Open full notes">
                                      <IconButton
                                        size="small"
                                        onClick={() => openLineTextPopout('Line item notes', editedItem.notes ?? '')}
                                        aria-label="Open full line item notes"
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <>
                                  <Typography
                                    variant="inherit"
                                    component="span"
                                    sx={{
                                      lineHeight: 1.2,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      flex: 1,
                                      minWidth: 0
                                    }}
                                  >
                                    {item.notes || '—'}
                                  </Typography>
                                  {!!(item.notes && String(item.notes).trim()) && (
                                    <Tooltip title="Open full notes">
                                      <IconButton
                                        size="small"
                                        onClick={() => openLineTextPopout('Line item notes', item.notes)}
                                        aria-label="Open full line item notes"
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <IconButton size="small" color="primary" onClick={() => handleSaveLineItem(item.id)} disabled={saving} title="Save">
                                  <Save fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEditLineItem} disabled={saving} title="Cancel">
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  disabled={!canEditLineItems}
                                  title={canEditLineItems ? 'Edit line' : 'Edit order header to change line items'}
                                  onClick={() => canEditLineItems && handleStartEditLineItem(item)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <Tooltip
                                  title={
                                    item.on_packing_slip
                                      ? 'This line is on a packing slip and cannot be removed.'
                                      : 'Delete'
                                  }
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={!canEditLineItems || item.on_packing_slip}
                                      aria-label="Delete line item"
                                      onClick={() => canEditLineItems && !item.on_packing_slip && handleRequestDeleteLineItem(item.id)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {newServiceDraftRows.map(({ item, index: draftIndex }, dIdx) => {
                      const newCode = item.service_id
                        ? effectiveServiceCode({ service_code: item.service_code, id: item.service_id })
                        : '';
                      return (
                        <TableRow key={`new-svc-${draftIndex}`}>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{partLines.length + serviceLines.length + dIdx + 1}</Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Box sx={orderTmLineItemIdxBoxSx}>{newCode || '—'}</Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                            <LineItemServiceAutocomplete
                              valueId={item.service_id}
                              onCommit={(v, svc) => handleLineItemChange(draftIndex, 'item_ref', v, svc)}
                              label="Service"
                              showNewService
                              noMargin
                              textFieldSx={{ '& .MuiInputBase-root': { fontSize: '0.8125rem' } }}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity_required}
                              onChange={(e) => handleLineItemChange(draftIndex, 'quantity_required', e.target.value)}
                              inputProps={{ min: 0, step: 'any' }}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              size="small"
                              value={item.unit || 'HR'}
                              onChange={(e) => handleLineItemChange(draftIndex, 'unit', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={orderTmLineBodyCellSx}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unit_price}
                              onChange={(e) => handleLineItemChange(draftIndex, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          {renderOrderLineTaxCells({
                            row: item,
                            isEditing: true,
                            disabled: saving || !editing,
                            onLineTaxPatch: (patch) => handleNewOrderLineTaxPatch(draftIndex, patch)
                          })}
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'right' }}>
                            {formatCurrency(orderLineSubtotalColumnAmount(item), '$0.00')}
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.notes || ''}
                                onChange={(e) => handleLineItemChange(draftIndex, 'notes', e.target.value)}
                                placeholder="Notes"
                                sx={{ flex: 1, minWidth: 0 }}
                              />
                              {String(item.notes || '').trim() !== '' && (
                                <Tooltip title="Open full notes">
                                  <IconButton
                                    size="small"
                                    onClick={() => openLineTextPopout('Line item notes', item.notes || '')}
                                    aria-label="Open full line item notes"
                                  >
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...orderTmLineBodyCellSx, textAlign: 'center' }}>
                            <IconButton size="small" color="error" onClick={() => handleRemoveLineItem(draftIndex)} title="Remove">
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(partLines.length > 0 || serviceLines.length > 0 || newLineItems.length > 0 || !isHistoricalOrder) && (
                      <>
                        <TableRow className="wm-line-totals-footer wm-line-totals-footer--subtotal" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={ORDER_TM_FOOTER_LABEL_COLSPAN} align="left" sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                              {orderLineItemsTaxEditActive && (
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={orderTaxScopeToggleValue}
                                  disabled={saving}
                                  onChange={(_, v) => {
                                    if (v != null) void applyOrderTaxScopePreset(v);
                                  }}
                                  aria-label="Order tax scope"
                                  sx={{
                                    flexShrink: 0,
                                    '& .MuiToggleButton-root': {
                                      px: 0.75,
                                      py: 0.35,
                                      fontSize: '0.6875rem',
                                      textTransform: 'none',
                                      lineHeight: 1.25
                                    }
                                  }}
                                >
                                  <ToggleButton value="line_items">Tax line items</ToggleButton>
                                  <ToggleButton value="order">Tax order</ToggleButton>
                                  <ToggleButton value="no_tax">No tax</ToggleButton>
                                </ToggleButtonGroup>
                              )}
                              <Typography
                                component="span"
                                sx={{ flex: 1, textAlign: 'right', fontWeight: 700, fontSize: '0.875rem', minWidth: 0 }}
                              >
                                Line items total
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatCurrency(lineItemsGrandTotal, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell colSpan={ORDER_TM_FOOTER_LABEL_COLSPAN} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            Discount
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {orderDisplayDiscount > 0
                              ? `−${formatCurrency(orderDisplayDiscount, '$0.00')}`
                              : formatCurrency(0, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle', py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', py: 0 }}>
                              <FormControl size="small" margin="none" sx={{ minWidth: 100 }} disabled={isHistoricalOrder || saving}>
                                <Select
                                  value={orderDiscountDraft.mode}
                                  displayEmpty
                                  onChange={(e) => {
                                    const mode = normalizeOrderDiscountModeUi(e.target.value);
                                    const next = { ...orderDiscountDraft, mode };
                                    setOrderDiscountDraft(next);
                                    void saveOrderDiscount(next);
                                  }}
                                  sx={{ fontSize: '0.8125rem', height: 32 }}
                                >
                                  <MenuItem value="none">None</MenuItem>
                                  <MenuItem value="percent">Percent</MenuItem>
                                  <MenuItem value="amount">Amount</MenuItem>
                                </Select>
                              </FormControl>
                              {orderDiscountDraft.mode === 'percent' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  aria-label="Discount percent"
                                  sx={documentFooterTaxRatePercentFieldSx}
                                  value={orderDiscountDraft.percentStr}
                                  disabled={isHistoricalOrder || saving}
                                  onChange={(e) =>
                                    setOrderDiscountDraft((d) => ({
                                      ...d,
                                      percentStr: sanitizeTaxPercentInputValue(e.target.value)
                                    }))
                                  }
                                  onBlur={() => {
                                    const next = {
                                      ...orderDiscountDraft,
                                      percentStr:
                                        String(orderDiscountDraft.percentStr ?? '').trim() === ''
                                          ? ''
                                          : formatTaxPercentDisplayValue(orderDiscountDraft.percentStr)
                                    };
                                    setOrderDiscountDraft(next);
                                    void saveOrderDiscount(next);
                                  }}
                                  inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end" disableTypography sx={{ ml: 0, mr: 0 }}>
                                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1, userSelect: 'none' }}>
                                          %
                                        </Box>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              )}
                              {orderDiscountDraft.mode === 'amount' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  label="Amount"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 104, ...documentFooterTaxFieldSx }}
                                  value={orderDiscountDraft.fixedStr}
                                  disabled={isHistoricalOrder || saving}
                                  onChange={(e) => setOrderDiscountDraft((d) => ({ ...d, fixedStr: e.target.value }))}
                                  onBlur={() => saveOrderDiscount()}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell colSpan={ORDER_TM_FOOTER_LABEL_COLSPAN} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {isPerLineOrderTax ? 'Line Items Tax Total' : 'Tax Total'}
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle', textAlign: 'right', py: 0.5 }}>
                            {formatCurrency(orderDisplayTax, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0', verticalAlign: 'middle', py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', py: 0 }}>
                              {orderTaxDraft.mode === 'percent' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  aria-label="Tax rate percent"
                                  sx={documentFooterTaxRatePercentFieldSx}
                                  value={orderTaxDraft.percentStr}
                                  disabled={isHistoricalOrder || saving || isPerLineOrderTax}
                                  onChange={(e) =>
                                    setOrderTaxDraft((d) => ({
                                      ...d,
                                      percentStr: sanitizeTaxPercentInputValue(e.target.value)
                                    }))
                                  }
                                  onBlur={() => {
                                    const next = {
                                      ...orderTaxDraft,
                                      percentStr:
                                        String(orderTaxDraft.percentStr ?? '').trim() === ''
                                          ? ''
                                          : formatTaxPercentDisplayValue(orderTaxDraft.percentStr)
                                    };
                                    setOrderTaxDraft(next);
                                    void saveOrderTax(next);
                                  }}
                                  inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end" disableTypography sx={{ ml: 0, mr: 0 }}>
                                        <Box component="span" sx={{ fontSize: '0.8125rem', lineHeight: 1, userSelect: 'none' }}>
                                          %
                                        </Box>
                                      </InputAdornment>
                                    )
                                  }}
                                />
                              )}
                              {orderTaxDraft.mode === 'fixed' && (
                                <TextField
                                  margin="none"
                                  size="small"
                                  label="Amount"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 104, ...documentFooterTaxFieldSx }}
                                  value={orderTaxDraft.fixedStr}
                                  disabled={isHistoricalOrder || saving || isPerLineOrderTax}
                                  onChange={(e) => setOrderTaxDraft((d) => ({ ...d, fixedStr: e.target.value }))}
                                  onBlur={() => saveOrderTax()}
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow className="wm-line-totals-footer" sx={{ bgcolor: '#eceff1' }}>
                          <TableCell colSpan={ORDER_TM_FOOTER_LABEL_COLSPAN} align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            Total (incl. tax)
                          </TableCell>
                          <TableCell align="right" sx={{ border: '1px solid #e0e0e0', fontWeight: 700, fontSize: '0.875rem', textAlign: 'right' }}>
                            {formatCurrency(orderDisplayGrandTotal, '$0.00')}
                          </TableCell>
                          <TableCell colSpan={3} sx={{ border: '1px solid #e0e0e0' }} />
                        </TableRow>
                      </>
                    )}
                  </>
                ) : null}

                    {isHistoricalOrder && (!order.lineItems || order.lineItems.length === 0) && newLineItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={ORDER_TM_LINE_TABLE_COLS} align="center" sx={{ border: '1px solid #e0e0e0', py: 3 }}>
                          <Typography variant="body2" color="textSecondary">
                            No line items.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  </Table>
                </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === orderDetailTabs.shipping && (
        <Box>
          {partLines.length > 0 ? (
            <Card sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 0.35,
                    lineHeight: 1.2
                  }}
                >
                  Fulfillment & shipping
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', mb: 0.5, lineHeight: 1.25 }}>
                  Packing slips · open · shipped (ship date)
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    backgroundColor: '#eceff1',
                    border: '1px solid #90a4ae',
                    borderLeft: '4px solid #546e7a',
                    borderRadius: 0.5,
                    px: 0.5,
                    py: 0.4
                  }}
                >
                  <TableContainer sx={{ maxHeight: 360, overflowX: 'auto', maxWidth: '100%' }}>
                    <Table size="small" sx={orderFulfillmentTableSx}>
                      <colgroup>
                        <col style={{ width: fulfillLW.num }} />
                        <col style={{ width: fulfillLW.code }} />
                        <col style={{ width: fulfillLW.part }} />
                        <col style={{ width: fulfillLW.qty }} />
                        <col style={{ width: fulfillLW.onPs }} />
                        <col style={{ width: fulfillLW.open }} />
                        <col style={{ width: fulfillLW.shipped }} />
                        <col style={{ width: fulfillLW.lineTotal }} />
                      </colgroup>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ border: 'none', borderBottom: '1px solid #78909c', py: '3px', px: '6px', fontSize: fulfillSectionBarFont, fontWeight: 700, bgcolor: '#546e7a', color: '#fff' }}>
                            Parts and materials
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', overflow: 'hidden' }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', overflow: 'hidden' }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae' }}>Part</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', textAlign: 'right' }}>Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', textAlign: 'right' }} title="Qty on packing slip">On PS</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', textAlign: 'right' }}>Open</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', textAlign: 'right' }}>Shipped</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#cfd8dc', color: '#37474f', fontSize: fulfillColHeaderFont, border: 'none', borderBottom: '1px solid #90a4ae', textAlign: 'right' }}>Line $</TableCell>
                        </TableRow>
                        {partLines.map((item, pIdx) => (
                          <TableRow key={`fulfill-part-${item.id}`}>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', overflow: 'hidden' }}>{pIdx + 1}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.part_number || '—'}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{item.part_name ?? item.description ?? '—'}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_required ?? 0)}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity_shipped ?? 0)}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', textAlign: 'right' }}>{getOpenToShipQty(item)}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', textAlign: 'right' }}>{getShippedQty(item)}</TableCell>
                            <TableCell sx={{ bgcolor: '#fff', border: 'none', borderBottom: '1px solid #eceff1', textAlign: 'right' }}>{formatCurrency(item.line_total, '$0.00')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent sx={{ py: 1.5, px: 1.25, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">
                  {isScopeOfWorkOrder(order)
                    ? 'No part lines to show for this Field Work order.'
                    : 'No part lines to show. Add part lines on the Line Items tab to see fulfillment quantities here.'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {activeTab === orderDetailTabs.addresses && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, m: 0 }}>
                Bill-to &amp; ship-to
              </Typography>
              {order?.customer_id ? (
                <Button size="small" variant="outlined" onClick={() => setOrderAddressesModalOpen(true)}>
                  Manage customer addresses
                </Button>
              ) : null}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Choose saved addresses from the customer record or enter a one-off ship-to. The ship-to is used for packing slips and invoices and should match where goods are delivered (tax and freight depend on it).
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Bill to
                </Typography>
                {editing && editedOrder ? (
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Billing address</InputLabel>
                    <Select
                      value={editedOrder.billing_customer_address_id || ''}
                      label="Billing address"
                      onChange={handleOrderBillingAddressSelect}
                    >
                      <MenuItem value="">Customer profile (default)</MenuItem>
                      {orderCustomerAddresses.map((addr) => (
                        <MenuItem key={addr.id} value={String(addr.id)}>
                          {[
                            (addr.address_type || '').toLowerCase() === 'billing' ? '[Bill] ' : '',
                            addr.address_line1,
                            [addr.city, addr.state].filter(Boolean).join(', ')
                          ]
                            .filter(Boolean)
                            .join(' ') || `Address #${addr.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {[
                    order?.billing_address_line1 || order?.address_line1,
                    order?.billing_address_line2 || order?.address_line2,
                    [order?.billing_city || order?.city, order?.billing_state || order?.state, order?.billing_zip || order?.zip_code]
                      .filter(Boolean)
                      .join(' ')
                      .trim(),
                    order?.billing_country
                  ]
                    .filter(Boolean)
                    .join('\n') || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Ship to
                </Typography>
                {editing && editedOrder ? (
                  <>
                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                      <InputLabel>Shipping address</InputLabel>
                      <Select
                        value={editedOrderShippingSelectValue}
                        label="Shipping address"
                        onChange={handleOrderShippingSelect}
                      >
                        <MenuItem value="">None (set when creating packing slip)</MenuItem>
                        {orderCustomerAddresses.map((addr) => (
                          <MenuItem key={addr.id} value={String(addr.id)}>
                            {[
                              (addr.address_type || '').toLowerCase() === 'shipping' ? '[Ship] ' : '',
                              addr.address_line1,
                              [addr.city, addr.state].filter(Boolean).join(', ')
                            ]
                              .filter(Boolean)
                              .join(' ') || `Address #${addr.id}`}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Other (enter manually)</MenuItem>
                      </Select>
                    </FormControl>
                    {editedOrderShippingSelectValue === 'custom' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <TextField
                          size="small"
                          label="Address line 1"
                          value={editedOrder.shipping_manual_line1}
                          onChange={(e) =>
                            setEditedOrder({ ...editedOrder, shipping_manual_line1: e.target.value, shipping_customer_address_id: '' })
                          }
                        />
                        <TextField
                          size="small"
                          label="Address line 2"
                          value={editedOrder.shipping_manual_line2}
                          onChange={(e) => setEditedOrder({ ...editedOrder, shipping_manual_line2: e.target.value })}
                        />
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              size="small"
                              label="City"
                              value={editedOrder.shipping_manual_city}
                              onChange={(e) => setEditedOrder({ ...editedOrder, shipping_manual_city: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <TextField
                              size="small"
                              label="State"
                              value={editedOrder.shipping_manual_state}
                              onChange={(e) => setEditedOrder({ ...editedOrder, shipping_manual_state: e.target.value })}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <TextField
                              size="small"
                              label="ZIP"
                              value={editedOrder.shipping_manual_zip}
                              onChange={(e) => setEditedOrder({ ...editedOrder, shipping_manual_zip: e.target.value })}
                            />
                          </Grid>
                        </Grid>
                        <TextField
                          size="small"
                          label="Country"
                          value={editedOrder.shipping_manual_country}
                          onChange={(e) => setEditedOrder({ ...editedOrder, shipping_manual_country: e.target.value })}
                        />
                      </Box>
                    )}
                  </>
                ) : null}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {[
                    order?.shipping_address_line1,
                    order?.shipping_address_line2,
                    [order?.shipping_city, order?.shipping_state, order?.shipping_zip].filter(Boolean).join(' ').trim(),
                    order?.shipping_country
                  ]
                    .filter(Boolean)
                    .join('\n') || '—'}
                </Typography>
              </Grid>
            </Grid>
            {editing && orderEditSaveBar ? <Box sx={{ mt: 1.5 }}>{orderEditSaveBar}</Box> : null}
          </CardContent>
        </Card>
      )}

      {activeTab === orderDetailTabs.comments && (() => {
        const commentsNotes = editing && editedOrder ? editedOrder.notes : (order.notes || '');
        return (
          <Card>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 0 }}>
                  Comments
                </Typography>
                {String(commentsNotes || '').trim() !== '' && (
                  <Tooltip title="Open full notes">
                    <IconButton
                      size="small"
                      onClick={() => openLineTextPopout('Comments', commentsNotes)}
                      aria-label="Open full comments"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={commentsNotes}
                onChange={handleOrderFieldChange('notes')}
                disabled={!editing}
                placeholder="Add comments here..."
                margin="dense"
                size="small"
              />
              {orderEditSaveBar ? <Box sx={{ mt: 1.5 }}>{orderEditSaveBar}</Box> : null}
            </CardContent>
          </Card>
        );
      })()}

      {activeTab === orderDetailTabs.shipments && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, m: 0 }}>
                Shipments (packing slips)
              </Typography>
              <Button size="small" variant="outlined" onClick={handleOpenCreatePackingSlip} disabled={isHistoricalOrder}>
                Create packing slip
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Customer and vendor outbound slips for this order. Ship date is set when a slip is marked shipped.
            </Typography>
            {sortedPackingSlipsForInvoiceTab.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No packing slips yet. Use Create packing slip above or the buttons in the order header.
              </Typography>
            ) : (
              <TableContainer sx={{ maxWidth: '100%' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Packing slip</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Ship status</TableCell>
                      <TableCell>Carrier / tracking</TableCell>
                      <TableCell>Customer invoice</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPackingSlipsForInvoiceTab.map((slip) => {
                      const linked = invoicesByPackingSlipId.get(String(slip.id));
                      const shipped = !!slip.shipped_date;
                      return (
                        <TableRow key={slip.id} hover>
                          <TableCell>
                            <Button
                              size="small"
                              variant="text"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={() => navigate(`/packing-slips/${slip.id}`)}
                            >
                              {slip.identifier ||
                                (slip.packing_slip_number != null ? `PS #${slip.packing_slip_number}` : `#${slip.id}`)}
                            </Button>
                          </TableCell>
                          <TableCell>{packingSlipPurposeLabel(slip.shipment_purpose)}</TableCell>
                          <TableCell>
                            {shipped ? (
                              <Chip size="small" label="Shipped" color="success" variant="outlined" />
                            ) : (
                              <Chip size="small" label="Not shipped" color="default" variant="outlined" />
                            )}
                            {shipped && slip.shipped_date ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatDateOnly(slip.shipped_date, 'MM/dd/yyyy')}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                              {[slip.carrier, slip.tracking_number].filter(Boolean).join(' · ') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {linked ? (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                onClick={() => navigate(`/customer-invoices/${linked.id}`)}
                              >
                                {linked.identifier || linked.invoice_number || `#${linked.id}`}
                              </Button>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === orderDetailTabs.invoices && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>
              Invoices and packing slips
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5, alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Order total
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(orderInvoiceFinancialTotals.orderTotal, '$0.00')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Total invoiced
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(orderInvoiceFinancialTotals.totalInvoiced, '$0.00')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Order − invoices
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color:
                      orderInvoiceFinancialTotals.invoicingMatchesOrder
                        ? 'success.main'
                        : orderInvoiceFinancialTotals.uninvoicedVsOrder > 0.02
                          ? 'warning.main'
                          : 'text.primary'
                  }}
                >
                  {orderInvoiceFinancialTotals.invoicingMatchesOrder
                    ? formatCurrency(0, '$0.00')
                    : formatCurrency(orderInvoiceFinancialTotals.uninvoicedVsOrder, '$0.00')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Outstanding balance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: orderInvoiceFinancialTotals.outstanding > 0 ? 'error.main' : 'text.primary'
                  }}
                >
                  {formatCurrency(orderInvoiceFinancialTotals.outstanding, '$0.00')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 40, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={
                    orderInvoiceFinancialTotals.invoicingMatchesOrder
                      ? 'Invoice totals match order'
                      : orderInvoiceFinancialTotals.uninvoicedVsOrder > 0.02
                        ? 'Not fully invoiced'
                        : orderInvoiceFinancialTotals.uninvoicedVsOrder < -0.02
                          ? 'Invoiced over order total'
                          : 'Compare order vs invoices'
                  }
                  color={
                    orderInvoiceFinancialTotals.invoicingMatchesOrder
                      ? 'success'
                      : orderInvoiceFinancialTotals.uninvoicedVsOrder > 0.02
                        ? 'warning'
                        : orderInvoiceFinancialTotals.uninvoicedVsOrder < -0.02
                          ? 'info'
                          : 'default'
                  }
                  variant="outlined"
                />
                {orderInvoiceFinancialTotals.hasPastDueInvoice ? (
                  <Chip size="small" label="Past due (invoice or schedule)" color="error" variant="outlined" />
                ) : null}
              </Box>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Not yet shipped (customer)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Lines with required quantity not yet on a <strong>shipped</strong> customer packing slip (no slip, slip without ship date, or partial).
            </Typography>
            {notYetShippedLineRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                All line quantities are fully covered by customer packing slips that have a ship date.
              </Typography>
            ) : (
              <TableContainer sx={{ mb: 2, maxWidth: '100%' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell align="right">Required</TableCell>
                      <TableCell align="right">On slip</TableCell>
                      <TableCell align="right">Shipped (dated)</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notYetShippedLineRows.map((row) => (
                      <TableRow key={row.li.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.label}
                          </Typography>
                          {row.sub && row.sub !== row.label ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {row.sub}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{formatQuantityDisplay(row.req ?? 0)}</TableCell>
                        <TableCell align="right">{formatQuantityDisplay(row.onSlip ?? 0)}</TableCell>
                        <TableCell align="right">{formatQuantityDisplay(row.dated ?? 0)}</TableCell>
                        <TableCell align="right">{formatQuantityDisplay(row.remaining ?? 0)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                            {row.reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Customer invoices
            </Typography>
            {orderInvoices.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No invoices for this order yet.
              </Typography>
            ) : (
              <TableContainer sx={{ mb: 2, maxWidth: '100%' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Invoice date</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Packing slip</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderInvoices.map((inv) => {
                      const total = Number(inv.total_amount) || 0;
                      const paid = Number(inv.amount_paid) || 0;
                      const balance = Math.max(0, total - paid);
                      const slipLabel =
                        inv.packing_slip_identifier ||
                        (inv.packing_slip_number != null ? `PS #${inv.packing_slip_number}` : null);
                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell>
                            <Button
                              size="small"
                              variant="text"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                            >
                              {inv.identifier || inv.invoice_number || `#${inv.id}`}
                            </Button>
                          </TableCell>
                          <TableCell>{inv.invoice_date ? formatDateOnly(inv.invoice_date, 'MM/dd/yyyy') : '—'}</TableCell>
                          <TableCell>{inv.due_date ? formatDateOnly(inv.due_date, 'MM/dd/yyyy') : '—'}</TableCell>
                          <TableCell align="right">{formatCurrency(total, '$0.00')}</TableCell>
                          <TableCell align="right">{formatCurrency(paid, '$0.00')}</TableCell>
                          <TableCell align="right">{formatCurrency(balance, '$0.00')}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={inv.status || '—'}
                              color={getCustomerInvoiceStatusColor(inv.status)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {inv.packing_slip_id ? (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                onClick={() => navigate(`/packing-slips/${inv.packing_slip_id}`)}
                              >
                                {slipLabel || `Slip #${inv.packing_slip_id}`}
                              </Button>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>
              Packing slips
            </Typography>
            {sortedPackingSlipsForInvoiceTab.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No packing slips for this order.
              </Typography>
            ) : (
              <TableContainer sx={{ maxWidth: '100%' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Packing slip</TableCell>
                      <TableCell>Purpose</TableCell>
                      <TableCell>Ship status</TableCell>
                      <TableCell>Matched invoice</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPackingSlipsForInvoiceTab.map((slip) => {
                      const linked = invoicesByPackingSlipId.get(String(slip.id));
                      const shipped = !!slip.shipped_date;
                      return (
                        <TableRow key={slip.id} hover>
                          <TableCell>
                            <Button
                              size="small"
                              variant="text"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={() => navigate(`/packing-slips/${slip.id}`)}
                            >
                              {slip.identifier || (slip.packing_slip_number != null ? `PS #${slip.packing_slip_number}` : `#${slip.id}`)}
                            </Button>
                          </TableCell>
                          <TableCell>{packingSlipPurposeLabel(slip.shipment_purpose)}</TableCell>
                          <TableCell>
                            {shipped ? (
                              <Chip size="small" label="Shipped" color="success" variant="outlined" />
                            ) : (
                              <Chip size="small" label="Not shipped" color="default" variant="outlined" />
                            )}
                            {shipped && slip.shipped_date ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {formatDateOnly(slip.shipped_date, 'MM/dd/yyyy')}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {linked ? (
                              <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                onClick={() => navigate(`/customer-invoices/${linked.id}`)}
                              >
                                {linked.identifier || linked.invoice_number || `#${linked.id}`}
                              </Button>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === orderDetailTabs.lineage && (
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
            <QuoteOrderLineagePanel loading={lineageLoading} error={lineageError} data={lineageData} />
          </CardContent>
        </Card>
      )}

      {activeTab === orderDetailTabs.attachments && (
        <Card>
          <CardContent sx={{ p: 1.5 }}>
            <Box
              sx={{
                mb: 1.5,
                minHeight: 120,
                p: 2,
                border: '2px dashed',
                borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
                borderRadius: 2,
                bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                transition: 'border-color 0.15s ease, background-color 0.15s ease'
              }}
              onDragOver={handleAttachmentDragOver}
              onDragLeave={handleAttachmentDragLeave}
              onDrop={handleAttachmentDrop}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CloudUpload sx={{ fontSize: 40, opacity: isDraggingOverAttachment ? 1 : 0.6 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Drop files here
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    or use the Upload button · max 5 files, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Attachments: {attachments.length}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                component="label"
                size="small"
                startIcon={<AddIcon />}
              >
                Upload
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileInputChange}
                />
              </Button>
            </Box>
            {attachmentsError && (
              <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>
            )}
            {attachmentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : attachments.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No attachments yet. Drag and drop files onto the area above or use the Upload button.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell>From Quote?</TableCell>
                      <TableCell>Categories</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((att) => (
                      <TableRow key={att.id} hover>
                        <TableCell>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadAttachment(att);
                            }}
                            size="small"
                            sx={{ textTransform: 'none' }}
                          >
                            {att.file_name}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {att.file_size != null
                            ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {att.uploaded_at
                            ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {att.quote_id ? (
                            <Chip label="From Quote" size="small" color="info" />
                          ) : (
                            <Chip label="From Order" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <AttachmentCategoryChips
                            attachmentId={att.id}
                            categories={att.categories}
                            onUpdated={(row) =>
                              setAttachments((prev) =>
                                prev.map((a) => (a.id === row.id ? { ...a, ...row } : a))
                              )
                            }
                            onError={(msg) => setAttachmentsError(msg)}
                          />
                        </TableCell>
                        <TableCell>
                          {att.quote_id == null ? (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteAttachment(att.id)}
                              aria-label="Delete attachment"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Delete from quote
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={lineTextPopoutOpen} onClose={closeLineTextPopout} maxWidth="md" fullWidth>
        <DialogTitle>{lineTextPopoutTitle || 'Line item details'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            multiline
            fullWidth
            minRows={8}
            value={lineTextPopoutValue}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineTextPopout}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceDraftOpen} onClose={() => !invoiceDraftSubmitting && closeInvoiceDraft()} maxWidth="md" fullWidth>
        <DialogTitle>Create invoice</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <FormLabel sx={{ display: 'block', mb: 0.75, fontSize: '0.875rem', fontWeight: 600 }}>Invoice type</FormLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={invoiceDraftKind}
              onChange={(e, next) => {
                if (!next || invoiceDraftSubmitting) return;
                if (next === 'line_items' && invoiceDraftLines.length === 0) return;
                setInvoiceDraftKind(next);
                if (next === 'payment_schedule' && invoiceDraftScheduleRows.length === 0) {
                  const todayYmd = formatDateOnly(new Date(), 'yyyy-MM-dd') || new Date().toISOString().split('T')[0];
                  setInvoiceDraftScheduleRows([
                    {
                      label: '',
                      amount: calculateScheduleRowRemainingAmount(invoiceDraftCapUninvoiced, []),
                        due_date: todayYmd,
                        _manualPctPrecision: false
                    }
                  ]);
                }
              }}
            >
              <ToggleButton value="line_items" disabled={invoiceDraftLines.length === 0 || invoiceDraftSubmitting}>
                Line items (remaining qty)
              </ToggleButton>
              <ToggleButton value="payment_schedule" disabled={invoiceDraftSubmitting}>
                Payment schedule
              </ToggleButton>
            </ToggleButtonGroup>
            {invoiceDraftLines.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                No remaining line quantities to invoice; use payment schedule for deposits or milestones (schedule row amounts are limited by
                remaining billable subtotal on the order; shipping and tax may exceed the order total).
              </Typography>
            ) : null}
          </Box>
          {invoiceDraftCapUninvoiced != null && invoiceDraftCapUninvoiced > 0.02 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Remaining billable subtotal on this order (sum of invoice subtotals vs order sell total):{' '}
              <strong>{formatCurrency(invoiceDraftCapUninvoiced, '$0.00')}</strong>
            </Typography>
          )}
          {invoiceDraftKind === 'line_items' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Quantities are limited to the remaining amount per line. Unit price and description come from the order line.
              </Typography>
              <TableContainer sx={{ maxHeight: 360, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    '& .MuiTableCell-root': { verticalAlign: 'middle', pt: 0.1, pb: 0.35, px: 0.6 },
                    '& .MuiTableBody-root .MuiTableRow-root': { height: 36 }
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Item</TableCell>
                      <TableCell align="center">Invoiced</TableCell>
                      <TableCell align="center">Remaining</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="center" sx={{ minWidth: 124 }}>
                        Unit $
                      </TableCell>
                      <TableCell align="center">Line $</TableCell>
                      <TableCell align="center">Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceDraftLines.map((row, idx) => {
                      const code =
                        row.item_type === 'service' ? effectiveServiceCode(row) || '—' : row.part_number || '—';
                      const maxQty = Math.max(0, parseFloat(row.quantity_remaining) || 0);
                      const currentQty = Math.max(0, parseFloat(row.quantity) || 0);
                      const compactFieldSx = {
                        width: 88,
                        '& .MuiOutlinedInput-root': {
                          height: 28,
                          fontSize: '0.8125rem'
                        },
                        '& .MuiOutlinedInput-input': {
                          py: 0.2,
                          px: 0.6
                        }
                      };
                      const descFieldSx = {
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: 28,
                          fontSize: '0.8125rem'
                        },
                        '& .MuiOutlinedInput-input': {
                          py: 0.2,
                          px: 0.6,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      };
                      const cellBox = (align, children) => (
                        <Box
                          sx={{
                            minHeight: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                            fontSize: '0.8125rem'
                          }}
                        >
                          {children}
                        </Box>
                      );
                      return (
                        <TableRow key={row.work_order_item_id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{cellBox('left', code)}</TableCell>
                          <TableCell align="right">{cellBox('right', row.quantity_invoiced)}</TableCell>
                          <TableCell align="right">{cellBox('right', row.quantity_remaining)}</TableCell>
                          <TableCell align="right" sx={{ minWidth: 98 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => bumpInvoiceDraftQty(idx, -1)}
                                disabled={currentQty <= 0}
                                sx={{ p: 0.25 }}
                                aria-label="Decrease quantity by 1"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                type="number"
                                value={row.quantity}
                                onChange={(e) => updateInvoiceDraftLine(idx, 'quantity', e.target.value)}
                                inputProps={{ min: 0, step: 0.001, max: maxQty, style: { textAlign: 'center' } }}
                                sx={{ ...compactFieldSx, width: 46, '& .MuiOutlinedInput-input': { textAlign: 'center', py: 0.2, px: 0.6 } }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => bumpInvoiceDraftQty(idx, 1)}
                                disabled={currentQty >= maxQty}
                                sx={{ p: 0.25 }}
                                aria-label="Increase quantity by 1"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 124 }}>
                            <TextField
                              size="small"
                              type="text"
                              value={(Math.max(0, parseFloat(row.unit_price) || 0)).toFixed(2)}
                              onChange={(e) => updateInvoiceDraftLine(idx, 'unit_price', e.target.value)}
                              onBlur={() =>
                                updateInvoiceDraftLine(
                                  idx,
                                  'unit_price',
                                  (Math.max(0, parseFloat(row.unit_price) || 0)).toFixed(2)
                                )
                              }
                              inputProps={{ inputMode: 'decimal' }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              sx={{ ...compactFieldSx, width: 124 }}
                              disabled
                            />
                          </TableCell>
                          <TableCell align="right">{cellBox('right', formatCurrency(row.line_total, '$0.00'))}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <TextField
                              size="small"
                              fullWidth
                              value={row.description ?? ''}
                              onChange={(e) => updateInvoiceDraftLine(idx, 'description', e.target.value)}
                              placeholder="Description"
                              sx={descFieldSx}
                              disabled
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Line merchandise: {formatCurrency(invoiceDraftLineMoney.merchandise, '$0.00')}
                </Typography>
                {invoiceDraftLineMoney.discount > 0.005 && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                    Order discount (applied on invoice): −{formatCurrency(invoiceDraftLineMoney.discount, '$0.00')}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Invoice subtotal: {formatCurrency(invoiceDraftLineMoney.subtotal, '$0.00')}
                  {(invoiceDraftLineMoney.shipping > 0 || invoiceDraftLineMoney.tax > 0) && (
                    <>
                      {' '}
                      · With shipping &amp; tax: {formatCurrency(invoiceDraftLineMoney.total, '$0.00')}
                    </>
                  )}
                </Typography>
                {invoiceDraftLineMoney.discount > 0.005 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Unit prices stay at order rates; the order discount is applied as an invoice header reduction so the bill matches the discounted order total.
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              {existingPaymentScheduleInvoices.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    A Payment Schedule Invoice already exists for this Order. Click to edit below or proceed to create a new, separate
                    Invoice for the remaining balance.
                  </Typography>
                  <List dense disablePadding>
                    {existingPaymentScheduleInvoices.map((inv) => {
                      const label = inv.identifier || inv.invoice_number || `Customer invoice #${inv.id}`;
                      return (
                        <ListItem key={inv.id} disablePadding sx={{ display: 'block' }}>
                          <ListItemButton
                            component={RouterLink}
                            to={`/customer-invoices/${inv.id}`}
                            onClick={() => closeInvoiceDraft()}
                            sx={{ py: 0.5, px: 1, borderRadius: 1 }}
                          >
                            <Typography variant="body2">{label}</Typography>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enter each installment or milestone. If schedule amounts exceed remaining billable subtotal on the order,
                the order discount is applied on the invoice header automatically. Shipping and tax may push the invoice total above the order total.
              </Typography>
              {invoiceDraftScheduleMoney.discount > 0.005 && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Schedule merchandise {formatCurrency(invoiceDraftScheduleMoney.merchandise, '$0.00')} exceeds remaining billable{' '}
                    {formatCurrency(invoiceDraftCapUninvoiced, '$0.00')}. Invoice will include a{' '}
                    {formatCurrency(invoiceDraftScheduleMoney.discount, '$0.00')} discount so the subtotal matches the order.
                  </Alert>
                )}
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Due date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Amount
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, minWidth: 110 }}>
                        %
                      </TableCell>
                      <TableCell align="center" sx={{ width: 72, fontWeight: 600 }}>
                        {' '}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceDraftScheduleRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.label ?? ''}
                            onChange={(e) => updateInvoiceDraftScheduleRow(idx, 'label', e.target.value)}
                            placeholder="e.g. Down payment"
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <TextField
                            size="small"
                            type="date"
                            value={row.due_date ?? ''}
                            onChange={(e) => updateInvoiceDraftScheduleRow(idx, 'due_date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          <CurrencyTextField
                            size="small"
                            value={row.amount}
                            onChange={(e) => handleInvoiceDraftScheduleAmountChange(idx, e.target.value)}
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                            dollarAdornment
                            inputProps={{ style: { textAlign: 'right' } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 110 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={invoiceDraftSchedulePercentDrafts[getInvoiceDraftScheduleRowKey(row, idx)] ?? getInvoiceDraftSchedulePercentDisplay(row)}
                            onFocus={() => handleInvoiceDraftSchedulePercentDraftFocus(idx, row)}
                            onBlur={() => handleInvoiceDraftSchedulePercentDraftBlur(idx, row)}
                            onChange={(e) => handleInvoiceDraftSchedulePercentDraftChange(idx, row, e.target.value)}
                            inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>
                            }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => removeInvoiceDraftScheduleRow(idx)}
                            disabled={invoiceDraftScheduleRows.length <= 1}
                            aria-label="Remove row"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button size="small" startIcon={<AddIcon />} onClick={addInvoiceDraftScheduleRow} sx={{ mt: 1 }} disabled={invoiceDraftSubmitting}>
                Add row
              </Button>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Schedule merchandise: {formatCurrency(invoiceDraftScheduleMoney.merchandise, '$0.00')}
                  {invoiceDraftScheduleMoney.discount > 0.005 && (
                    <>
                      {' '}
                      · Discount: −{formatCurrency(invoiceDraftScheduleMoney.discount, '$0.00')}
                    </>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Invoice subtotal: {formatCurrency(invoiceDraftScheduleMoney.subtotal, '$0.00')} · With shipping &amp; tax:{' '}
                  {formatCurrency(invoiceDraftScheduleMoney.total, '$0.00')}
                </Typography>
              </Box>
            </>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Shipping ($)"
                type="number"
                value={invoiceDraftShipping}
                onChange={(e) => setInvoiceDraftShipping(e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Tax % (of invoice subtotal)"
                type="number"
                value={invoiceDraftTaxPct}
                onChange={(e) => setInvoiceDraftTaxPct(e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Optional if you use fixed tax"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Tax amount ($)"
                type="number"
                value={invoiceDraftTaxAmt}
                onChange={(e) => setInvoiceDraftTaxAmt(e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Overrides tax % if both set"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeInvoiceDraft} disabled={invoiceDraftSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submitInvoiceDraft}
            variant="contained"
            color="primary"
            disabled={
              invoiceDraftSubmitting ||
              (invoiceDraftKind === 'line_items' && invoiceDraftLines.length === 0)
            }
          >
            {invoiceDraftSubmitting ? 'Saving…' : 'Create invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomerAddressesModal
        open={orderAddressesModalOpen}
        onClose={() => {
          setOrderAddressesModalOpen(false);
          const cid = order?.customer_id;
          if (cid) {
            addressService
              .getByCustomerId(cid)
              .then((res) => setOrderCustomerAddresses(res.data || []))
              .catch(() => {});
          }
        }}
        customerId={order?.customer_id}
        customerName={
          order?.company_name ||
          [order?.customer_first_name, order?.customer_last_name].filter(Boolean).join(' ').trim() ||
          ''
        }
      />

      <Dialog
        open={orderPrintSettingsOpen}
        onClose={() => setOrderPrintSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: '1.125rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            letterSpacing: '-0.01em',
            color: 'text.primary'
          }}
        >
          Print version options
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.6, fontSize: '0.875rem' }}
          >
            Use Print work order to open the browser print dialog for the same FabWorks work order document as the
            toolbar print icon—not this popup. Options below apply only to that document for this print. The toolbar
            print button uses the same default layout (single line-items table; comments and description hidden). Uncheck
            the boxes below to include them. Nothing here is saved.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover'
            }}
          >
            <FormControlLabel
              sx={{
                m: 0,
                mr: 0,
                alignItems: 'center',
                gap: 0.75,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  fontWeight: 400,
                  paddingTop: 0
                }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={printSettingsDraft.hideComments}
                  onChange={(e) =>
                    setPrintSettingsDraft((d) => ({ ...d, hideComments: e.target.checked }))
                  }
                  sx={{ p: 0.75 }}
                  inputProps={{ 'aria-label': 'Hide Comments on printed work order' }}
                />
              }
              label="Hide Comments"
            />
            <FormControlLabel
              sx={{
                m: 0,
                mr: 0,
                alignItems: 'center',
                gap: 0.75,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  color: 'text.secondary',
                  fontWeight: 400,
                  paddingTop: 0
                }
              }}
              control={
                <Checkbox
                  size="small"
                  checked={printSettingsDraft.hideDescription}
                  onChange={(e) =>
                    setPrintSettingsDraft((d) => ({ ...d, hideDescription: e.target.checked }))
                  }
                  sx={{ p: 0.75 }}
                  inputProps={{ 'aria-label': 'Hide Description on printed work order' }}
                />
              }
              label="Hide Description"
            />
            {!isScopeOfWorkOrder(order) ? (
              <FormControlLabel
                sx={{
                  m: 0,
                  mr: 0,
                  alignItems: 'flex-start',
                  gap: 0.75,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    fontWeight: 400,
                    paddingTop: 0
                  }
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={printSettingsDraft.separateLineSections}
                    onChange={(e) =>
                      setPrintSettingsDraft((d) => ({ ...d, separateLineSections: e.target.checked }))
                    }
                    sx={{ p: 0.75, pt: 0.35 }}
                    inputProps={{ 'aria-label': 'Print parts and labor as separate tables' }}
                  />
                }
                label="Separate line items into two tables (Parts and Materials, then Labor and Services), each with its own header row"
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button variant="text" color="inherit" onClick={() => setOrderPrintSettingsOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Print />}
            onClick={handlePrintFromSettingsDialog}
          >
            Print work order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialogs */}
      <Dialog
        open={revisionDialogOpen}
        onClose={() => setRevisionDialogOpen(false)}
        aria-labelledby="order-revision-history-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="order-revision-history-dialog-title">
          Previous Order Revisions
        </DialogTitle>
        <DialogContent>
          {revisionDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisionDialogItems.length === 0 ? (
            <DialogContentText>No previous revisions found.</DialogContentText>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {revisionDialogItems.map((rev) => (
                  <TableRow key={rev.id}>
                    <TableCell>{rev.identifier}</TableCell>
                    <TableCell>{rev.order_date ? formatDateOnly(rev.order_date, 'MM/dd/yyyy') : '—'}</TableCell>
                    <TableCell>
                      <Chip label={rev.status || '—'} size="small" sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>{formatCurrency(rev.total_amount, '—')}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setRevisionDialogOpen(false);
                          navigate(`/work-orders/${rev.id}`);
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>{getDeleteOrderDialogBody()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteOrder}
            color="error"
            variant="contained"
            autoFocus
            disabled={deleteOrderConfirmDisabled}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discountInvoiceWarnOpen}
        onClose={() => setDiscountInvoiceWarnOpen(false)}
      >
        <DialogTitle>Update discount?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This order has {unpaidNonVoidedInvoices.length} unpaid invoice
            {unpaidNonVoidedInvoices.length === 1 ? '' : 's'}. Changing the discount updates the billable order total.
            Void and reissue those invoices so they match the new discount.
          </DialogContentText>
          <List dense sx={{ mt: 1 }}>
            {unpaidNonVoidedInvoices.map((inv) => (
              <ListItem key={inv.id} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/customer-invoices/${inv.id}`}
                  onClick={() => setDiscountInvoiceWarnOpen(false)}
                >
                  {inv.identifier || inv.invoice_number || `#${inv.id}`}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountInvoiceWarnOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void saveOrderDiscount(orderDiscountDraft, { skipWarn: true })}
          >
            Save discount anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={copyOrderDialogOpen} onClose={() => !copyOrderBusy && setCopyOrderDialogOpen(false)}>
        <DialogTitle>Copy order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {`Create a new order with the same customer, header fields, line items, and tax settings as Order #${
              order?.identifier || order?.work_order_number || id
            }?`}
            {'\n\n'}
            The new order will start as Open, with no quote link, no packing slips or shipments, and no invoice. Line
            quantities will reset to unissued (quantity used and procurement received amounts start at zero). The Lineage tab on
            the new order will show this order as its parent.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOrderDialogOpen(false)} disabled={copyOrderBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmDuplicateOrder} disabled={copyOrderBusy} autoFocus>
            {copyOrderBusy ? 'Copying…' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteLineItemDialogOpen}
        onClose={() => setDeleteLineItemDialogOpen(false)}
      >
        <DialogTitle>Delete Line Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this line item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLineItemDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteLineItem} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnToStockOpen} onClose={() => !returnToStockSaving && (setReturnToStockOpen(false), setReturnToStockItem(null))} maxWidth="xs" fullWidth>
        <DialogTitle>Return to stock</DialogTitle>
        <DialogContent>
          {returnToStockItem && (
            <>
              <DialogContentText sx={{ mb: 1 }}>
                Part: {returnToStockItem.part_number ?? returnToStockItem.part_name ?? '—'}. Issued: {returnToStockItem.quantity_used ?? 0}.
              </DialogContentText>
              <TextField
                fullWidth
                size="small"
                label="Quantity to return"
                type="number"
                value={returnToStockQty}
                onChange={(e) => setReturnToStockQty(e.target.value)}
                inputProps={{ min: 0.01, max: returnToStockItem.quantity_used || 0, step: 0.01 }}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Reason"
                value={returnToStockReason}
                onChange={(e) => setReturnToStockReason(e.target.value)}
                placeholder="e.g. over-issued, job cancelled"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnToStockOpen(false)} disabled={returnToStockSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleReturnToStock} disabled={returnToStockSaving || !returnToStockQty}>
            {returnToStockSaving ? <CircularProgress size={24} /> : 'Return'}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordPaymentDialog
        open={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        onSuccess={() => void refreshOrderDependencies(order?.id ?? id)}
        title={`Record Payment for ${order?.identifier || order?.work_order_number || `Order #${id}`}`}
        initialCustomerId={order?.customer_id}
        initialWorkOrderId={id}
        initialWorkOrderLabel={order?.identifier || order?.work_order_number || `#${id}`}
        initialAmount={(Number(orderInvoiceFinancialTotals?.outstanding) || 0) > 0 ? Number(orderInvoiceFinancialTotals.outstanding).toFixed(2) : ''}
        lockCustomer
        lockWorkOrder
      />

      <NewPartModal
        open={newPartModalOpen}
        onClose={() => {
          setNewPartModalOpen(false);
          setNewPartModalRowIndex(null);
          setNewPartModalItemId(null);
        }}
        onCreated={handleNewPartCreated}
      />

      <NewServiceModal
        open={newServiceModalOpen}
        onClose={() => {
          setNewServiceModalOpen(false);
          setNewServiceModalRowIndex(null);
          setNewServiceModalItemId(null);
        }}
        onCreated={handleNewServiceCreated}
      />
      </Box>
    </>
  );
};

export default OrderDetail;
